import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import {
  GetEntityQuery,
  GetAssessmentQuery,
  GetAssessmentStandardFrameworkQuery,
  FrameworkScoresByAssessmentQuery,
  GroupsByRootIdQuery,
  LabelType,
  TagsByEntityIdQuery,
  ModelEntityFilterInput,
  ModelAssessmentStandardFrameworkFilterInput,
  EntityTypeEnum,
  EntityParentQuery,
  FileTypeEnum,
  StandardType,
} from '../API.service';
import { CUSTOMAPIService } from '../custom-api.service';
import { environment } from 'environments/environment';
import { UtilsService } from './utils.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FileService } from './file.service';
import { DomainFrameworkService } from './domain-framework.service';
import { AuthSessionCacheService } from './auth-session-cache.service';
import { AdditionalRequirements } from './enums/common.enum';
import { AppLevelRoleEnum } from './enums/appLevelRoles.enum';

// A single framework score item from FrameworkScoresByAssessment
export type FrameworkScoreItem = FrameworkScoresByAssessmentQuery['items'][0];

// Pre-computed filter sets derived from ASF filterMetadata — no S3 fetch required
export interface FrameworkMeta {
  functions: Set<string>;
  controls: Set<string>;
  domains: Set<string>;
}

// All data stored for a single framework within an assessment
export interface FrameworkEntry {
  asf: any;
  score: FrameworkScoreItem | null;
  tree: any | null;
  groupedTree: any | null;
  listQuestions: any[];
  loadedQuestionIds: Set<string>;
  metadata: FrameworkMeta;
  treeLoaded: boolean;
  groupedTreeLoaded: boolean;
  scoreLoaded: boolean;
}

// All data stored for a single assessment (one sub-entity's active assessment)
export interface AssessmentEntry {
  subEntity: GetEntityQuery;
  childEntityId: string;
  subEntityName: string;
  frameworks: { [frameworkKey: string]: FrameworkEntry };
  scoresLoaded: boolean;
  questionsDbLoaded: boolean;
  questionDbData: any[];
  assessment: any;
  risks: any[];
  risksLoaded: boolean;
}

export interface TagsStore {
  all: any[];
  byType: { [type in LabelType]?: any[] };
  byTargetId: { [targetId: string]: any[] };
  byParentTagId: { [parentTagId: string]: any[] };
  byAssessmentId: { [assessmentId: string]: any };
  loadedKeys: Set<string>;
}

export interface CategoriesStore {
  all: any[];
  byType: { [type in LabelType]?: any[] };
  byTargetId: { [targetId: string]: any[] };
  byCategoryId: { [categoryId: string]: any[] };
  byAssessmentId: { [assessmentId: string]: any };
  // Same namespaced fetch-tracking as TagsStore.loadedKeys.
  loadedKeys: Set<string>;
}

export interface AlertsStore {
  byIndustry: { [industry: string]: any[] };
  loadedIndustries: Set<string>;
}

@Injectable({ providedIn: 'root' })
export class RootEntityContextService {
  private _entityId: string | null = null;
  private _entity: GetEntityQuery | null = null;

  private _assessmentMap: { [assessmentId: string]: AssessmentEntry } = {};
  private _subEntitiesLoadedByParent: { [parentId: string]: boolean } = {};

  private _groups: any[] = [];
  private _groupsLoaded = false;

  private _graphs: any[] = [];
  private _graphsLoaded = false;

  private _riskGroupsStore: { [entityId: string]: { groups: any[]; loaded: boolean } } = {};

  private _tagsStore: TagsStore = {
    all: [],
    byType: {},
    byTargetId: {},
    byParentTagId: {},
    byAssessmentId: {},
    loadedKeys: new Set<string>(),
  };
  private _categoriesStore: CategoriesStore = {
    all: [],
    byType: {},
    byTargetId: {},
    byCategoryId: {},
    byAssessmentId: {},
    loadedKeys: new Set<string>(),
  };
  private _alertsStore: AlertsStore = {
    byIndustry: {},
    loadedIndustries: new Set<string>(),
  };
  private _subEntityToAssessment: { [subEntityId: string]: string } = {};
  private _completionByAssessment: { [assessmentId: string]: any[] } = {};

  // In-flight de-dup for GetAssessment so concurrent callers share a single request.
  private _assessmentInFlight: { [assessmentId: string]: Promise<GetAssessmentQuery | null> } = {};
  // In-flight S3 tree fetches, keyed by every argument that changes what the fetch produces, so two
  // callers asking for the same tree at the same time share one request instead of both hitting S3.
  private _treeInFlight: { [cacheKey: string]: Promise<any> } = {};
  private _scoresInFlight: { [assessmentId: string]: Promise<void> } = {};
  private _completionInFlight: { [assessmentId: string]: Promise<any[]> } = {};
  private _listEntitiesInFlight: { [parentId: string]: Promise<GetEntityQuery[]> } = {};

  //* for SubEntity access control, if the user has full access to all sub-entities, _fullAccess is true and _accessEntityIds is empty.
  //* If the user has limited access, _fullAccess is false and _accessEntityIds contains the list of sub-entity IDs the user has access to.
  private _accessEntityIds = new Set<string>();
  private _fullAccess = true;

  //* Vendor visibility mirrors the legacy ThirdPartyService.getExtendVendorsListByParentId scoping,
  //* which keys off the ROLE's vendorIds (NOT entityIds). Captured via setAccessContext; null pre-login.
  private _vendorScope: { vendorIds: string[]; isRootEntityAccess: boolean; isDefaultRole: boolean } | null = null;

  selectionPath: string[] = [];
  readonly NA_SCORES: number[] = [999, 998];

  // ─── Real-time subscription subjects ────────────────────────────────────────
  // Consumers can subscribe to these to react to AppSync push events without
  // polling. All subjects emit the raw AppSync payload for the given record type.

  readonly onEntityUpdated = new Subject<any>();
  readonly onAssessmentUpdated = new Subject<any>();
  readonly onFrameworkScoreUpdated = new Subject<any>();
  readonly onFrameworkScoreCreated = new Subject<any>();
  readonly onASFUpdated = new Subject<any>();
  readonly onASFCreated = new Subject<any>();
  // Emits on any risk create/update/delete pushed via AppSync (backend- or client-originated).
  // The `type` lets consumers reconcile their local lists without re-deriving the operation.
  readonly onRiskUpdated = new Subject<{ type: 'created' | 'updated' | 'deleted'; risk: any }>();
  // Emits on any risk group create/update/delete pushed via AppSync.
  readonly onRiskGroupUpdated = new Subject<{ type: 'created' | 'updated' | 'deleted'; group: any }>();
  // Emits when a Group (pillars/reporting group) is updated — e.g. the calcGroupScores lambda
  // recalculating group scores after answers/tags change.
  readonly onGroupUpdated = new Subject<any>();

  // Active AppSync subscription handles — one per listener type.
  private _entitySub: Subscription | null = null;
  private _assessmentSub: Subscription | null = null;
  private _frameworkScoreSub: Subscription | null = null;
  private _frameworkScoreCreateSub: Subscription | null = null;
  private _asfSub: Subscription | null = null;
  private _asfCreateSub: Subscription | null = null;
  private _riskCreateSub: Subscription | null = null;
  private _riskUpdateSub: Subscription | null = null;
  private _riskDeleteSub: Subscription | null = null;
  private _riskGroupCreateSub: Subscription | null = null;
  private _riskGroupUpdateSub: Subscription | null = null;
  private _riskGroupDeleteSub: Subscription | null = null;
  private _groupUpdateSub: Subscription | null = null;
  private _vendorsDetailSub: Subscription | null = null;
  private _rootEntitySub: Subscription | null = null;

  constructor(
    private customApi: CUSTOMAPIService,
    private route: ActivatedRoute,
    private router: Router,
    private fileService: FileService,
    private domainFrameworkService: DomainFrameworkService,
    private authSessionCacheService: AuthSessionCacheService
  ) {
    // Re-subscribe when the WebSocket reconnects, tear down when it disconnects.
    this.customApi.webSocketConnectionListner$.subscribe(hubState => {
      if (hubState?.isConnectionClosed) {
        this.teardownSubscriptions();
      } else if (!hubState?.isConnectionClosed && !this._entitySub && this._entityId) {
        void this._initSubscriptionsInternal();
      }
    });
  }

  reset(): void {
    this._entityId = null;
    this._entity = null;
    this._assessmentMap = {};
    this._listEntitiesInFlight = {};
    this._subEntitiesLoadedByParent = {};
    this._scoresInFlight = {};
    this._groups = [];
    this._groupsLoaded = false;
    this._graphs = [];
    this._graphsLoaded = false;
    this._riskGroupsStore = {};
    this._tagsStore = {
      all: [],
      byType: {},
      byTargetId: {},
      byParentTagId: {},
      byAssessmentId: {},
      loadedKeys: new Set<string>(),
    };
    this._categoriesStore = {
      all: [],
      byType: {},
      byTargetId: {},
      byCategoryId: {},
      byAssessmentId: {},
      loadedKeys: new Set<string>(),
    };
    this._alertsStore = {
      byIndustry: {},
      loadedIndustries: new Set<string>(),
    };
    this._subEntityToAssessment = {};
    this._completionByAssessment = {};
    this._completionInFlight = {};
    this._assessmentInFlight = {};
    this._treeInFlight = {};
    this.selectionPath = [];
  }

  /**
   * Getters and Setters:
   */
  setAssessmentForSubEntity(subEntityId: string, assessmentId: string): void {
    this._subEntityToAssessment[subEntityId] = assessmentId;
  }

  getAssessmentIdBySubEntityId(subEntityId: string): string | null {
    return this._subEntityToAssessment[subEntityId] ?? null;
  }

  // Getter for getting the subEntities List against the root entity
  async getSubEntities(entityType?: string, entityId?: string): Promise<any[]> {
    // Build the ROLE-SCOPED list from the cache. Scoping lives here so every consumer that calls
    // getSubEntities() automatically gets a result appropriate to the current user's role.
    const build = (): any[] => {
      const entries = Object.values(this._assessmentMap);
      const list = entries
        .filter(entry => {
          const et = entry.subEntity?.entityType;
          // Explicit type -> exact match. No type -> first-party contract = everything EXCEPT vendors
          // (vendors share _assessmentMap; vendor consumers must use getVendors()).
          return entityType ? et === entityType : et !== EntityTypeEnum.VENDOR;
        })
        .map(entry => entry.subEntity);
      return this.scopeToRole(list);
    };

    // Fast path: sub-entities already loaded for this root — no API call needed.
    if (this._entityId && this._subEntitiesLoadedByParent[this._entityId]) {
      return build();
    }

    if (!this._entityId) {
      this._entityId = entityId || UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    }
    if (this._entityId) {
      await this.listEntitiesByParentId(this._entityId as string);
    }
    if (this._entityId && this._assessmentMap) {
      return build();
    }
    return [];
  }

  /**
   * Feed RECS the current user's identity/role once it's resolved. Called by AuthService on login /
   * role change / logout. This is the ONLY place "who am I" enters the entity-scoping flow.
   */
  setAccessContext(
    user: { entityIds?: string[] } | null,
    role: {
      name?: string;
      isRootEntityAccess?: boolean;
      defaultOrEntityId?: string;
      entityIds?: string[];
      vendorIds?: string[];
    } | null
  ): void {
    this._fullAccess = this.isFullAccessRole(role);
    this._accessEntityIds = new Set<string>([...(user?.entityIds || []), ...(role?.entityIds || [])]);
    this._vendorScope = role
      ? {
          vendorIds: role.vendorIds || [],
          isRootEntityAccess: !!role.isRootEntityAccess,
          isDefaultRole: (role.defaultOrEntityId || '').toLowerCase() === 'default',
        }
      : null;
  }

  // Mirror of AuthService.isNotFromHigherRoles() (inverted): admin / mssp / super entity leader /
  // entity leader (default roles) and root-access custom roles get everything; everything else is
  // scoped to assigned entities. Super Entity Leader belongs here because its entityIds hold ROOT
  // entity ids (like Entity Leader) — its restriction is applied at the root-entity list, not here.
  private isFullAccessRole(role: any): boolean {
    if (!role?.name) {
      return true;
    }
    const name = role.name.toLowerCase();
    if (role.defaultOrEntityId === 'default') {
      return (
        name === AppLevelRoleEnum.ADMIN ||
        name === AppLevelRoleEnum.MSSP ||
        name === AppLevelRoleEnum.SUPER_ENTITY_LEADER ||
        name === AppLevelRoleEnum.ENTITY_LEADER
      );
    }
    return !!role.isRootEntityAccess;
  }

  /**
   * THE single role-scoping implementation. Full-access roles see everything; scoped roles see only the
   * entities assigned to them (union of the user's entityIds and their role's entityIds).
   */
  scopeToRole(entities: any[]): any[] {
    if (this._fullAccess) {
      return entities || [];
    }
    return (entities || []).filter(entity => this._accessEntityIds.has(entity.id));
  }

  /**
   * Role-scoped list that ALSO drops entities without an active assessment/frameworks — the exact
   * contract the old EntityService.filterEntitiesBasedOnRole had. Used by non-getSubEntities() paths
   * (remediation, users-settings via listChildEntitysByParentId).
   */
  filterEntitiesBasedOnRole(entities: any[]): any[] {
    return this.scopeToRole(entities).filter(entity => entity?.activeAssessment?.standardFrameworkList?.items?.length);
  }

  /**
   * Role-scope a vendor list. Mirrors the legacy ThirdPartyService.getExtendVendorsListByParentId
   * decision tree exactly (keyed off the ROLE's vendorIds, not entityIds):
   *  - non-default, root-access role WITH explicit vendorIds -> only those ids (unless it includes 'all')
   *  - default role -> all vendors
   *  - anything else -> no vendor access
   */
  scopeVendorsToRole(vendors: any[]): any[] {
    const scope = this._vendorScope;
    // No role context yet (e.g. pre-login) — don't hide data; mirrors isFullAccessRole(null) === true.
    if (!scope) {
      return vendors || [];
    }
    if (scope.vendorIds.length && !scope.isDefaultRole && scope.isRootEntityAccess) {
      if (scope.vendorIds.includes('all')) {
        return vendors || [];
      }
      const allowed = new Set(scope.vendorIds);
      return (vendors || []).filter(v => allowed.has(v?.id));
    }
    if (scope.isDefaultRole) {
      return vendors || [];
    }
    return [];
  }

  /**
   * Getter for the third-party VENDOR list under a root entity. Single entry point for vendors — the
   * first-party getSubEntities() no longer returns them. Vendors are stored in _assessmentMap like any
   * child entity; this only lazily loads them once per root (deduped via _listEntitiesInFlight) and
   * returns a role-scoped view. Use getSubEntity(id) for a single vendor by id.
   * @param entityId root/parent id; falls back to the current root or the route param.
   * @param isExtended when true, resolves each vendor's presigned logo URL (parity with the old
   *        ThirdPartyService.getExtendVendorsListByParentId isExtended flag).
   */
  async getVendors(entityId?: string, isExtended = true): Promise<any[]> {
    const rootId = entityId || this._entityId || UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    if (!rootId) {
      return [];
    }
    // Vendors are child entities (entityType VENDOR) under the same root — adopt it if not set yet.
    this._entityId = this._entityId ?? rootId;

    // Read VENDOR entities straight from the shared _assessmentMap (no parallel store) + role-scope.
    const buildScoped = (): any[] =>
      this.scopeVendorsToRole(
        Object.values(this._assessmentMap)
          .map(entry => entry.subEntity)
          .filter(sub => sub?.entityType === EntityTypeEnum.VENDOR)
      );

    if (this._subEntitiesLoadedByParent[`${rootId}:VENDOR`]) {
      return buildScoped();
    }
    // Dedup concurrent loads via the shared in-flight map, namespaced so it can't collide with the
    // first-party child load (keyed by the bare parentId). The same :VENDOR-namespaced key gates the
    // shared loaded-map, giving vendors their own lazy-load flag without a dedicated field.
    const inFlightKey = `${rootId}:VENDOR`;
    if (!this._listEntitiesInFlight[inFlightKey]) {
      this._listEntitiesInFlight[inFlightKey] = (async () => {
        try {
          await this.loadVendorsByParentId(rootId, isExtended);
        } catch (e) {
          console.log('Error fetching vendors by parentId:', e);
        } finally {
          // Mark loaded either way so a transient failure doesn't trigger a refetch storm.
          this._subEntitiesLoadedByParent[inFlightKey] = true;
          delete this._listEntitiesInFlight[inFlightKey];
        }
        return [];
      })() as Promise<GetEntityQuery[]>;
    }
    await this._listEntitiesInFlight[inFlightKey];
    return buildScoped();
  }

  // Paginate the VENDOR children of a root via EntityParent (NOTE: EntityParent, not EntitiesByParent —
  // only EntityParent's projection includes vendorDetails, which the third-party dashboards need. With
  // EntitiesByParent vendorDetails is null and the overview fires a per-vendor GetEntity retry storm).
  // Optionally resolve logos, and register each vendor into _assessmentMap. Pure loader — caching/scoping
  // handled by getVendors().
  private async loadVendorsByParentId(parentId: string, isExtended: boolean): Promise<void> {
    let vendorList: GetEntityQuery[] = [];
    let nextToken = null;
    const frameworkFilter = this.getVisibleAssessmentStandardFrameworkFilter();
    const vendorFilter: ModelEntityFilterInput = { entityType: { eq: EntityTypeEnum.VENDOR } };
    do {
      const entityResponse: EntityParentQuery = await this.customApi.EntityParent(
        parentId,
        null,
        vendorFilter,
        environment.queryListLimit,
        nextToken,
        frameworkFilter
      );
      const hydratedItems = await this.hydrateStandardFrameworkLists(
        entityResponse.items as GetEntityQuery[],
        frameworkFilter
      );
      vendorList = vendorList.concat(hydratedItems as any);
      nextToken = entityResponse.nextToken;
    } while (nextToken);
    let vendors = vendorList?.length ? vendorList : [];
    if (isExtended) {
      vendors = await this.getLogoUrlForEntities(vendors);
    }
    // Register vendors into _assessmentMap (keyed by assessmentId) — same store as first-party children.
    this.populateAssessmentMapperForEntity(vendors as GetEntityQuery[]);
    // Vendors share the first-party root — make sure root context + subscriptions are live.
    this._entityId = this._entityId ?? parentId;
    void this._initSubscriptionsInternal();
  }

  // Getter for getting the frameworks List against a particular assessmentId
  async getFrameworksByAssessmentId(
    assessmentId: string,
    type?: StandardType,
    isArchiveMode?: boolean,
    notAddedFrameworks?: boolean
  ): Promise<any[] | null> {
    try {
      if (!this._assessmentMap[assessmentId] || !this._assessmentMap[assessmentId].frameworks || notAddedFrameworks) {
        await this.getASFListByAssessmentId(
          assessmentId,
          notAddedFrameworks ? null : this.getVisibleAssessmentStandardFrameworkFilter()
        );
      }
      // The assessment may not belong to this first-party root (e.g. a third-party vendor), in which
      // case it was never registered in _assessmentMap — return empty rather than dereferencing undefined.
      const frameworksMap = this._assessmentMap[assessmentId]?.frameworks;
      if (!frameworksMap) {
        return [];
      }
      let filteredFrameworks = Object.values(frameworksMap);
      if (type) {
        filteredFrameworks = filteredFrameworks.filter(f => f.asf.type === type);
      }
      if (isArchiveMode === true) {
        filteredFrameworks = filteredFrameworks.filter(f => f.asf.archived);
      } else if (isArchiveMode === false) {
        filteredFrameworks = filteredFrameworks.filter(f => !f.asf.archived);
      }
      // Drop `not_added` catalog placeholders entirely. Emitting them (previously as `[]`) leaked
      // key-less entries that pass consumers' `!fw.not_added` filters and then crash on `fw.key.split(...)`.
      // Newly created sub-entities and sub-entity-leader views have all-`not_added` catalogs, so this was
      // a guaranteed crash for them in the collection framework mapper.
      if (!notAddedFrameworks) {
        return filteredFrameworks.filter(f => !f.asf.not_added).map(f => f.asf);
      }
      // in case of smart-mapping need to return all frameworks including
      //  not_added frameworks, so that they can be displayed in the smart-mapping component
      return filteredFrameworks.map(f => f.asf);
    } catch (e) {
      console.log('Error fetching frameworks by assessmentId:', e);
      return null;
    }
  }

  // Getter for getting the single AssessmentStandardFramework against ID.
  async getASFById(assessmentId: string, frameworkKey: string): Promise<any | null> {
    try {
      if (!this._assessmentMap[assessmentId] || !this._assessmentMap[assessmentId].frameworks) {
        await this.getASFListByAssessmentId(assessmentId, this.getVisibleAssessmentStandardFrameworkFilter());
      }
      const frameworks = this._assessmentMap[assessmentId]?.frameworks;
      // Cache is keyed by the full key, so BASE#N must match exactly.
      return frameworks?.[frameworkKey]?.asf ?? frameworks?.[frameworkKey?.split('#')[0]]?.asf ?? null;
    } catch (e) {
      console.log('Error fetching ASF by assessmentId and frameworkKey:', e);
      return null;
    }
  }

  // Upsert a single AssessmentStandardFramework into the assessment's framework cache. Merges into an
  // existing entry (preserving already-loaded score/tree/questions) or creates a fresh one. No-op if the
  // assessment isn't cached yet (the next read fetches fresh). Keyed by asf.key.
  addOrUpdateFrameworkInCache(assessmentId: string, asf: any): void {
    if (!assessmentId || !asf?.key) {
      return;
    }
    const entry = this._assessmentMap[assessmentId];
    if (!entry) {
      return;
    }
    if (!entry.frameworks) {
      entry.frameworks = {};
    }
    const name = this.domainFrameworkService.getFrameworkName(asf.key.split('#')[0]);
    const existing = entry.frameworks[asf.key];
    if (existing) {
      const prevAsf = existing.asf;
      existing.asf = { ...prevAsf, ...asf, name };
      if (asf.timeline == null && prevAsf?.timeline) {
        existing.asf.timeline = prevAsf.timeline;
      }
      if (asf.defaultQuestionSettings == null && prevAsf?.defaultQuestionSettings) {
        existing.asf.defaultQuestionSettings = prevAsf.defaultQuestionSettings;
      }
    } else if (!asf.id) {
      // A partial patch ({ key, … }) must never mint a cache entry: with `not_added`/`archived` absent the
      // phantom passes every `!fw.not_added` visibility filter and renders as a live framework card.
      return;
    } else {
      entry.frameworks[asf.key] = {
        asf: { ...asf, name },
        score: null,
        tree: null,
        groupedTree: null,
        listQuestions: [],
        loadedQuestionIds: new Set<string>(),
        metadata: {
          functions: new Set<string>(),
          controls: new Set<string>(),
          domains: new Set<string>(),
        },
        treeLoaded: false,
        groupedTreeLoaded: false,
        scoreLoaded: false,
      };
    }
  }

  /**
   * Resolve the real AssessmentStandardFramework behind a FrameworkScores row that arrived for a
   * framework we have not cached, then register it and land the score on it. `not_added` catalog
   * placeholders are deliberately dropped: the backend scores them like any other framework, so caching
   * them would surface a card for every suggested framework of a newly created sub-entity.
   */
  private async registerFrameworkFromScore(assessmentId: string, frameworkKey: string, score: any): Promise<void> {
    try {
      const asf: any = await this.customApi.GetAssessmentStandardFramework(`${assessmentId}#${frameworkKey}`);
      // The root may have been switched (cache reset) while the read was in flight.
      const entry = this._assessmentMap[assessmentId];
      if (!entry || !asf?.key || asf.not_added === true) {
        return;
      }
      this.addOrUpdateFrameworkInCache(assessmentId, asf);
      const newFw = entry.frameworks?.[asf.key];
      if (newFw) {
        if (score.groups) {
          score.groups = JSON.parse(score.groups);
        }
        if (score.functions) {
          score.functions = JSON.parse(score.functions);
        }
        newFw.score = score;
        newFw.scoreLoaded = true;
        this.syncTreeScoresFromFrameworkScore(newFw, score);
      }
    } catch (e) {
      console.log('Error resolving framework for a newly-created framework score:', e);
    }
  }

  // Remove a framework from the assessment cache (e.g. a replaced/deleted framework).
  removeFrameworkFromCache(assessmentId: string, frameworkKey: string): void {
    const entry = this._assessmentMap[assessmentId];
    if (entry?.frameworks?.[frameworkKey]) {
      delete entry.frameworks[frameworkKey];
    }
  }

  // Mirror the ASF.filterMetadata (written by the updateAssessmentStandardFrameworkTrigger lambda) into
  // the pre-computed metadata Sets on a FrameworkEntry. No-op when filterMetadata is absent.
  private applyFilterMetadata(fw: FrameworkEntry, filterMetadata: any): void {
    if (!fw || !filterMetadata) {
      return;
    }
    if (Array.isArray(filterMetadata.functions)) {
      fw.metadata.functions = new Set<string>(filterMetadata.functions);
    }
    if (Array.isArray(filterMetadata.controls)) {
      fw.metadata.controls = new Set<string>(filterMetadata.controls);
    }
    if (Array.isArray(filterMetadata.domains)) {
      fw.metadata.domains = new Set<string>(filterMetadata.domains);
    }
  }

  async getFrameworksBySubEntityId(subEntityId: string, type?: StandardType): Promise<any[] | null> {
    try {
      const assessmentId = this.getAssessmentIdBySubEntityId(subEntityId);
      if (!assessmentId) {
        console.log(`No assessmentId found for subEntityId: ${subEntityId}`);
        return null;
      }
      return await this.getFrameworksByAssessmentId(assessmentId, type);
    } catch (e) {
      console.log('Error fetching frameworks by subEntityId:', e);
      return null;
    }
  }

  /**
   * Fetches framework scores for given assessmentIds and populates
   * the FrameworkEntry.score field in _assessmentMap.
   * Handles pagination internally. Marks scoreLoaded=true per individual framework.
   * Only fetches scores for frameworks that are missing them (scoreLoaded: false).
   * In-flight deduplication: parallel callers for the same assessmentId share one fetch.
   */
  async getFrameworkScoresByAssessmentIds(assessmentIds: string[]): Promise<any> {
    try {
      // Flat list of every cached FrameworkScores row under the requested assessments.
      const collectScores = () =>
        assessmentIds.flatMap(assessmentId =>
          Object.values(this._assessmentMap[assessmentId]?.frameworks || {})
            .map((f: any) => f.score)
            .filter(Boolean)
        );

      // Identify which assessments have frameworks with missing scores.
      const assessmentsNeedingScores = assessmentIds.filter(assessmentId => {
        const frameworks = Object.values(this._assessmentMap[assessmentId]?.frameworks || {});
        return frameworks.length > 0 && frameworks.some((fw: any) => !fw.scoreLoaded);
      });

      // Fast path: all frameworks in all requested assessments already have scores loaded.
      if (assessmentsNeedingScores.length === 0) {
        return collectScores();
      }

      const promises = assessmentsNeedingScores.map(assessmentId => {
        // A fetch is already in-flight for this assessmentId — reuse it
        if (this._scoresInFlight[assessmentId]) {
          return this._scoresInFlight[assessmentId];
        }

        // Start a new fetch and register it so parallel callers can share it
        const fetch = (async () => {
          let nextToken: string = null;
          do {
            const result = await this.customApi.FrameworkScoresByAssessment(assessmentId, null, null, null, nextToken);

            if (result?.items?.length) {
              result.items.forEach(frameworkScore => {
                // frameworkScore.id = "assessmentId_FRAMEWORK_KEY"
                const frameworkKey = frameworkScore.id.split('_').slice(1).join('_');

                if (this._assessmentMap[assessmentId]?.frameworks[frameworkKey]) {
                  if (frameworkScore.groups) {
                    frameworkScore.groups = JSON.parse(frameworkScore.groups);
                  }
                  if (frameworkScore.functions) {
                    frameworkScore.functions = JSON.parse(frameworkScore.functions);
                  }
                  this._assessmentMap[assessmentId].frameworks[frameworkKey].score = frameworkScore
                    ? { ...frameworkScore }
                    : null;
                  // Mark this framework as having its score loaded.
                  this._assessmentMap[assessmentId].frameworks[frameworkKey].scoreLoaded = true;
                }
              });
            }
            nextToken = result?.nextToken;
          } while (nextToken);
          Object.values(this._assessmentMap[assessmentId]?.frameworks || {}).forEach((fw: any) => {
            fw.scoreLoaded = true;
          });
          // Released via .finally() so a failed fetch can't wedge the in-flight slot.
        })().finally(() => delete this._scoresInFlight[assessmentId]);

        this._scoresInFlight[assessmentId] = fetch;
        return fetch;
      });

      await Promise.all(promises);

      // Return flat list of score items for all requested assessmentIds
      return collectScores();
    } catch (e) {
      console.log('Error fetching framework scores:', e);
      return [];
    }
  }

  // Read-only cache probe: true when the assessment's frameworks are registered and every one already
  // has its FrameworkScores row loaded (the getter's fast path). Never triggers a fetch or cache write.
  areFrameworkScoresLoaded(assessmentId: string): boolean {
    const frameworks = Object.values(this._assessmentMap[assessmentId]?.frameworks || {});
    return frameworks.length > 0 && frameworks.every((fw: any) => fw.scoreLoaded);
  }

  // Returns the cached score object for a specific framework.
  // Delegates unconditionally — the per-framework scoreLoaded guard makes the call a cache hit when loaded.
  async getFrameworkScoreByKey(assessmentId: string, frameworkKey: string): Promise<any | null> {
    await this.getFrameworkScoresByAssessmentIds([assessmentId]);
    return this._assessmentMap[assessmentId]?.frameworks[frameworkKey]?.score ?? null;
  }

  // Getter for getting the questionDatas against a particular assessmentId
  async getQuestionsDataByAssessmentId(assessmentId: string): Promise<any[] | null> {
    if (!assessmentId) {
      return null;
    }
    const entry = this._assessmentMap[assessmentId];
    if (entry?.questionsDbLoaded) {
      return entry.questionDbData;
    }
    const questions = await this.fetchQuestionsForAssessment(assessmentId);
    // Cache when the assessment belongs to the current root; otherwise (e.g. a vendor, or an assessment
    // being torn down that was never registered) just return the freshly-fetched list.
    if (this._assessmentMap[assessmentId]) {
      this._assessmentMap[assessmentId].questionDbData = questions;
      this._assessmentMap[assessmentId].questionsDbLoaded = true;
    }
    return questions;
  }

  // Write-through for the once-latched QuestionData cache: register a row created after the
  // initial load so later create-vs-update decisions don't re-CREATE an existing row
  // (DynamoDB ConditionalCheckFailedException).
  upsertQuestionDataInCache(assessmentId: string, questionData: any): void {
    if (!assessmentId || !questionData?.id) {
      return;
    }
    const entry = this._assessmentMap[assessmentId];
    if (!entry?.questionsDbLoaded || !Array.isArray(entry.questionDbData)) {
      return;
    }
    const idx = entry.questionDbData.findIndex(q => q.id === questionData.id);
    if (idx > -1) {
      entry.questionDbData[idx] = { ...entry.questionDbData[idx], ...questionData };
    } else {
      entry.questionDbData.push(questionData);
    }
  }

  // Getter for getting risks against particular assessmentIds
  async getRisksByAssessmentIds(assessmentIds: string[]): Promise<any[]> {
    const unloadedIds = assessmentIds.filter(id => !this._assessmentMap[id]?.risksLoaded);
    const fetched = unloadedIds.length ? await this.loadRisksByAssessmentIds(unloadedIds) : {};
    // Registered assessments are served from the cache; an assessment that isn't part of the current
    // root (so it can't be cached) falls back to its freshly-fetched list instead of an empty array.
    return assessmentIds.flatMap(id => this._assessmentMap[id]?.risks || fetched[id] || []);
  }

  private async loadRisksByAssessmentIds(assessmentIds: string[]): Promise<{ [assessmentId: string]: any[] }> {
    const fetched: { [assessmentId: string]: any[] } = {};
    try {
      await Promise.all(
        assessmentIds.map(async assessmentId => {
          let nextToken: string | null = null;
          const risks: any[] = [];
          do {
            const result = await this.customApi.RiskByAssessmentId(
              assessmentId,
              undefined,
              undefined,
              1000,
              nextToken || undefined
            );
            risks.push(...(result?.items || []));
            nextToken = result?.nextToken ?? null;
          } while (nextToken);

          fetched[assessmentId] = risks;
          if (this._assessmentMap[assessmentId]) {
            this._assessmentMap[assessmentId].risks = risks;
            this._assessmentMap[assessmentId].risksLoaded = true;
          }
        })
      );
    } catch (e) {
      console.log('Error fetching risks by assessmentIds:', e);
    }
    return fetched;
  }

  // Update (or insert) a single risk in the cached _assessmentMap risks list
  updateRiskInCache(risk: any): void {
    if (!risk?.assessmentId || !risk?.id) {
      return;
    }
    const entry = this._assessmentMap[risk.assessmentId];
    // Only patch when risks were actually loaded for this assessment
    if (entry?.risksLoaded && Array.isArray(entry.risks)) {
      const idx = entry.risks.findIndex(r => r.id === risk.id);
      if (idx > -1) {
        entry.risks[idx] = { ...entry.risks[idx], ...risk };
      } else {
        entry.risks.push(risk);
      }
    }
  }

  // Remove a single risk from the cached _assessmentMap risks list.
  removeRiskFromCache(risk: any): void {
    if (!risk?.assessmentId || !risk?.id) {
      return;
    }
    const entry = this._assessmentMap[risk.assessmentId];
    if (entry?.risksLoaded && Array.isArray(entry.risks)) {
      entry.risks = entry.risks.filter(r => r.id !== risk.id);
    }
  }

  /**
   * Invalidate the risks cache for an assessment. Forces refetch on next getRisksByAssessmentIds call.
   * Use this after bulk risk deletion/mutation during reassessment.
   */
  invalidateRisksForAssessment(assessmentId: string): void {
    if (!assessmentId || !this._assessmentMap[assessmentId]) {
      return;
    }
    // Clear the loaded flag so next getRisksByAssessmentIds call will refetch
    this._assessmentMap[assessmentId].risksLoaded = false;
    // Optionally clear the risks array to free memory
    this._assessmentMap[assessmentId].risks = [];
    console.log(`INFO: Risks cache invalidated for assessment ${assessmentId}`);
  }

  /**
   * Invalidate the risk groups cache for an entity. Forces refetch on next getRiskGroupsByEntityId call.
   * Use this after bulk risk group mutation during reassessment.
   */
  invalidateRiskGroupsForEntity(entityId: string): void {
    if (!entityId || !this._riskGroupsStore[entityId]) {
      return;
    }
    // Clear the loaded flag so next getRiskGroupsByEntityId call will refetch
    this._riskGroupsStore[entityId].loaded = false;
    // Optionally clear the groups array to free memory
    this._riskGroupsStore[entityId].groups = [];
    console.log(`INFO: Risk groups cache invalidated for entity ${entityId}`);
  }

  // Getter for getting risk groups against a root entity id.
  async getRiskGroupsByEntityId(entityId: string): Promise<any[]> {
    if (!this._riskGroupsStore[entityId]?.loaded) {
      await this.loadRiskGroupsByEntityId(entityId);
    }
    return this._riskGroupsStore[entityId]?.groups || [];
  }

  private async loadRiskGroupsByEntityId(entityId: string): Promise<void> {
    try {
      let groups: any[] = [];
      let nextToken: string | null = null;
      do {
        const response = await this.customApi.RiskGroupByEntityId(
          entityId,
          undefined,
          undefined,
          300,
          nextToken || undefined
        );
        groups = groups.concat(response?.items || []);
        nextToken = response?.nextToken ?? null;
      } while (nextToken);
      this._riskGroupsStore[entityId] = { groups, loaded: true };
    } catch (e) {
      console.log('Error fetching risk groups by entityId:', e);
      this._riskGroupsStore[entityId] = { groups: this._riskGroupsStore[entityId]?.groups || [], loaded: true };
    }
  }

  // Update (or insert) a single RAW risk group in the cache. The component also emits transformed
  // group rows (createRiskGroupObj output); those are ignored via the __typename guard so we never
  // pollute the raw cache — every group mutation also emits the raw API result, and createRiskGroupObj
  // rebuilds from raw fields on read, so a raw field-merge is equivalent to a fresh fetch.
  updateRiskGroupInCache(group: any): void {
    if (group?.__typename !== 'RiskGroup' || !group?.id || !group?.entityId) {
      return;
    }
    const entry = this._riskGroupsStore[group.entityId];
    if (entry?.loaded && Array.isArray(entry.groups)) {
      const idx = entry.groups.findIndex(g => g.id === group.id);
      if (idx > -1) {
        entry.groups[idx] = { ...entry.groups[idx], ...group };
      } else {
        entry.groups.push(group);
      }
    }
  }

  // Remove a single risk group from the cache (keyed by entityId + id).
  removeRiskGroupFromCache(id: string, entityId: string): void {
    const entry = this._riskGroupsStore[entityId];
    if (entry?.loaded && Array.isArray(entry.groups)) {
      entry.groups = entry.groups.filter(g => g.id !== id);
    }
  }

  // Getter for getting the list of tags against rootId and type
  async getTagsByEntityId(entityId: string, type?: LabelType, targetId?: string): Promise<any[] | null> {
    const key = `${type ?? 'all'}|${targetId ?? 'all'}`;
    if (!this._tagsStore.loadedKeys.has(key)) {
      await this.populateTagsByEntityId(entityId, type, targetId);
    }
    if (targetId) {
      const items = this._tagsStore.byTargetId[targetId] || [];
      return type ? items.filter(t => t.type === type) : items;
    }
    if (type) {
      return this._tagsStore.byType[type] || [];
    }
    return this._tagsStore.all || [];
  }

  // Getter for getting the list of categories against rootId and type
  async getCategoriesByEntityId(entityId: string, type?: LabelType, targetId?: string): Promise<any[] | null> {
    const key = `${type ?? 'all'}|${targetId ?? 'all'}`;
    if (!this._categoriesStore.loadedKeys.has(key)) {
      await this.populateCategoriesByEntityId(entityId, type, targetId);
    }
    if (targetId) {
      const items = this._categoriesStore.byTargetId[targetId] || [];
      return type ? items.filter(t => t.type === type) : items;
    }
    if (type) {
      return this._categoriesStore.byType[type] || [];
    }
    return this._categoriesStore.all || [];
  }

  // Getter for getting the list of tags against a particular assessmentFrameworkKey
  async getTagsByAssessmentFrameworkKey(assessmentId: string, frameworkKey: string): Promise<any[] | null> {
    const assessmentFrameworkKey = `${assessmentId}#${frameworkKey}`;
    if (!this._tagsStore.byAssessmentId[assessmentId] || !this._tagsStore.byAssessmentId[assessmentId][frameworkKey]) {
      await this.loadTagsByAssessmentFrameworkKey(assessmentId, frameworkKey);
    }
    return this._tagsStore.byAssessmentId[assessmentId]?.[frameworkKey] || null;
  }

  // Getter for getting the list of categories against a particular assessmentFrameworkKey
  async getCategoriesByAssessmentFrameworkKey(assessmentId: string, frameworkKey: string): Promise<any[] | null> {
    const assessmentFrameworkKey = `${assessmentId}#${frameworkKey}`;
    if (
      !this._categoriesStore.byAssessmentId[assessmentId] ||
      !this._categoriesStore.byAssessmentId[assessmentId][frameworkKey]
    ) {
      await this.loadCategoriesByAssessmentFrameworkKey(assessmentId, frameworkKey);
    }
    return this._categoriesStore.byAssessmentId[assessmentId]?.[frameworkKey] || null;
  }

  // Getter for getting the tag against the targetID.
  async getTagsByTargetId(targetId: string): Promise<any[] | null> {
    if (!this._tagsStore.loadedKeys.has(`target|${targetId}`)) {
      await this.populateTagsByTargetId(targetId);
    }
    return this._tagsStore.byTargetId[targetId] || null;
  }

  // Getter for getting the categories against the targetID (same `target|` gating as tags).
  async getCategoriesByTargetId(targetId: string): Promise<any[] | null> {
    if (!this._categoriesStore.loadedKeys.has(`target|${targetId}`)) {
      await this.populateCategoriesByTargetId(targetId);
    }
    return this._categoriesStore.byTargetId[targetId] || null;
  }

  // Getter for getting the child tags against a parentTagId.
  async getTagsByParentTagId(parentTagId: string): Promise<any[] | null> {
    if (!this._tagsStore.loadedKeys.has(`parent|${parentTagId}`)) {
      await this.populateTagsByParentTagId(parentTagId);
    }
    return this._tagsStore.byParentTagId[parentTagId] || null;
  }

  // Getter for getting the sub-categories against a categoryId (same `category|` gating).
  async getCategoriesByCategoryId(categoryId: string): Promise<any[] | null> {
    if (!this._categoriesStore.loadedKeys.has(`category|${categoryId}`)) {
      await this.populateCategoriesByCategoryId(categoryId);
    }
    return this._categoriesStore.byCategoryId[categoryId] || null;
  }

  // Getter for getting the assessment tree from S3 for a particular assessmentId and frameworkKey.
  // skipFlatten: when the caller only needs the raw tree (e.g. the artifacts screen does its own tree walk),
  // pass true to avoid the extra flatTreeList pass — getFlatListOfQuestions() rebuilds it lazily if needed.
  async getAssessmentTree(
    assessmentId: string,
    framework: GetAssessmentStandardFrameworkQuery,
    skipFlatten = false
  ): Promise<any | null> {
    if (
      !this._assessmentMap[assessmentId] ||
      !this._assessmentMap[assessmentId].frameworks[framework.key] ||
      !this._assessmentMap[assessmentId].frameworks[framework.key].treeLoaded
    ) {
      await this.getAssessmentTreeFromS3(assessmentId, framework, false, skipFlatten);
    }
    return this._assessmentMap[assessmentId] && this._assessmentMap[assessmentId].frameworks[framework.key]
      ? this._assessmentMap[assessmentId].frameworks[framework.key].tree
      : null;
  }

  // Getter for getting the grouped assessment tree from S3 for a particular assessmentId and frameworkKey
  async getAssessmentGroupedTree(assessmentId: string, frameworkKey: string): Promise<any | null> {
    const entry = this._assessmentMap[assessmentId]?.frameworks[frameworkKey];
    if (!entry || !entry.groupedTreeLoaded) {
      // getAssessmentTreeFromS3 fetches both regular and grouped trees in one call
      await this.getAssessmentTreeFromS3(
        assessmentId,
        { key: frameworkKey } as GetAssessmentStandardFrameworkQuery,
        true
      );
    }
    return this._assessmentMap[assessmentId]?.frameworks[frameworkKey]?.groupedTree ?? null;
  }

  // get Flatten list of questions for a particular assessmentId and frameworkKey
  async getFlatListOfQuestions(assessmentId: string, frameworkKey: string): Promise<any[] | null> {
    const fwEntry = this._assessmentMap[assessmentId]?.frameworks[frameworkKey];
    if (!this._assessmentMap[assessmentId] || !fwEntry || !fwEntry.treeLoaded) {
      await this.getAssessmentTreeFromS3(assessmentId, this._assessmentMap[assessmentId].frameworks[frameworkKey].asf);
    } else if (!fwEntry.listQuestions?.length && fwEntry.tree) {
      // Tree was cached without flattening (a skipFlatten caller such as the artifacts screen). Build the
      // flat list from the already-cached tree instead of re-downloading it.
      this.flatTreeList(
        fwEntry.tree,
        assessmentId,
        frameworkKey,
        this._assessmentMap[assessmentId]?.subEntityName || '',
        this._assessmentMap[assessmentId]?.questionDbData || [],
        this._assessmentMap[assessmentId]?.childEntityId || ''
      );
    }
    return this._assessmentMap[assessmentId] && this._assessmentMap[assessmentId].frameworks[frameworkKey]
      ? this._assessmentMap[assessmentId].frameworks[frameworkKey].listQuestions
      : [];
  }

  async getAssessment(assessmentId: string): Promise<GetAssessmentQuery | null> {
    if (!assessmentId) {
      return null;
    }
    const entry = this._assessmentMap[assessmentId];
    // 1. Assessment already fetched/stored — reuse it.
    const cached = entry?.assessment;
    if (cached?.standardFrameworkList?.items?.length) {
      return cached as GetAssessmentQuery;
    }
    // 2. Fall back to the assessment embedded on the cached sub-entity. EntityParent already returns
    //    activeAssessment with the assessment-level timeline AND per-framework timelines, so we can
    //    serve it from cache and skip the heavy GetAssessment round-trip.
    const embedded = entry?.subEntity?.activeAssessment;
    if (embedded?.standardFrameworkList?.items?.length) {
      entry.assessment = embedded;
      return embedded as GetAssessmentQuery;
    }
    if (this._assessmentInFlight[assessmentId]) {
      return this._assessmentInFlight[assessmentId];
    }
    const fetchPromise = (async () => {
      try {
        const assessment = await this.customApi.GetAssessment(assessmentId);
        if (assessment && this._assessmentMap[assessmentId]) {
          this._assessmentMap[assessmentId].assessment = assessment;
        }
        return assessment as GetAssessmentQuery;
      } catch (e) {
        console.log('Error fetching assessment by id:', e);
        return null;
      } finally {
        delete this._assessmentInFlight[assessmentId];
      }
    })();
    this._assessmentInFlight[assessmentId] = fetchPromise;
    return fetchPromise;
  }

  /**
   * Questionnaire completion records for an assessment, cached per assessmentId with in-flight
   * de-duplication so parallel callers (e.g. one preDataSetup() per sub-entity) share a single fetch.
   * Kept fresh by upsertQuestionnaireCompletion() from the live OnUpdateQuestionnaireCompletion event.
   */
  // Read-only cache probe for the questionnaire-completion store — never triggers a fetch.
  isQuestionnaireCompletionCached(assessmentId: string): boolean {
    return !!this._completionByAssessment[assessmentId];
  }

  async getQuestionnaireCompletionByAssessmentId(assessmentId: string): Promise<any[]> {
    if (!assessmentId) {
      return [];
    }
    if (this._completionByAssessment[assessmentId]) {
      return this._completionByAssessment[assessmentId];
    }
    if (this._completionInFlight[assessmentId]) {
      return this._completionInFlight[assessmentId];
    }
    const fetchPromise = (async () => {
      let results: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await this.customApi.CompletionByAssessment(assessmentId, null, null, 10000, nextToken);
        results = results.concat(result?.items ?? []);
        nextToken = result?.nextToken ?? null;
      } while (nextToken);
      this._completionByAssessment[assessmentId] = results;
      return results;
    })();
    this._completionInFlight[assessmentId] = fetchPromise;
    try {
      return await fetchPromise;
    } finally {
      delete this._completionInFlight[assessmentId];
    }
  }

  // Keep the cached completion list in sync with a live OnUpdateQuestionnaireCompletion event.
  upsertQuestionnaireCompletion(assessmentId: string, completion: any): void {
    if (!assessmentId || !completion) {
      return;
    }
    const list = this._completionByAssessment[assessmentId];
    if (!list) {
      return;
    }
    const index = list.findIndex(c => c.id === completion.id);
    if (index !== -1) {
      list[index] = completion;
    } else {
      list.push(completion);
    }
  }

  /**
   * All the API calls along with helpers are down here.
   */
  // Getter for getting alerts by industry — checks cache first, loads if not yet fetched
  async getAlertsByIndustry(industry: string): Promise<any[]> {
    if (!this._alertsStore.loadedIndustries.has(industry)) {
      await this.loadAlertsByIndustry(industry);
    }
    return this._alertsStore.byIndustry[industry] || [];
  }

  private async loadAlertsByIndustry(industry: string): Promise<void> {
    try {
      const alertResponse: any = await this.customApi.ListAlerts(industry, FileTypeEnum.ALERTS);
      const alertList = await alertResponse.body.json();
      this._alertsStore.byIndustry[industry] = alertList?.data || [];
      this._alertsStore.loadedIndustries.add(industry);
    } catch (e) {
      console.log('Error fetching alerts by industry:', e);
      this._alertsStore.byIndustry[industry] = [];
      this._alertsStore.loadedIndustries.add(industry);
    }
  }

  async getGroupsList(entityId: string): Promise<any> {
    try {
      if (this._groupsLoaded) {
        return this._groups;
      }
      const groups = await this.customApi.GroupsByRootId(entityId);
      this._groups = groups?.items?.length ? groups.items : [];
      this._groupsLoaded = true;
      return this._groups;
    } catch (e) {
      console.log('Cannot get the groups ', e);
      return [];
    }
  }

  // The dynamicLabelHandler lambda (DELETE) prunes groups server-side once they no longer have any
  // tag/category referencing them. Because the lambda decides deletions authoritatively against the DB,
  // the client cannot reliably know which groups were removed — so we drop the cache and let the next
  // getGroupsList() refetch the fresh, post-prune list.
  invalidateGroupsCache(): void {
    this._groups = [];
    this._groupsLoaded = false;
  }

  // ─── Reporting graphs (pillars → Graphs tab) ─────────────────────────────
  // Cached per root (single-root cache, reset on root switch). Loaded once, then kept coherent by the
  // create/update/delete wrappers below so the Graphs screen never re-queries GraphsByRootId on revisit.
  async getGraphsByRootId(rootId: string): Promise<any[]> {
    try {
      if (this._graphsLoaded) {
        return this._graphs;
      }
      let items: any[] = [];
      let nextToken: string | null = null;
      do {
        const response = await this.customApi.GraphsByRootId(rootId, null, null, environment.queryListLimit, nextToken);
        items = items.concat(response?.items || []);
        nextToken = response?.nextToken ?? null;
      } while (nextToken);
      this._graphs = items;
      this._graphsLoaded = true;
      return this._graphs;
    } catch (e) {
      console.log('Cannot get the graphs ', e);
      return [];
    }
  }

  // Create a graph and push it into the cache. Errors are intentionally NOT swallowed so the caller can
  // run its own failure handling (e.g. showing a "Failed" row).
  async createGraph(payload: any): Promise<any> {
    const result = await this.customApi.CreateGraph(payload);
    if (result && this._graphsLoaded) {
      this._graphs.push(result);
    }
    return result;
  }

  // Update a graph (scoped to its root) and patch the cached copy.
  async updateGraph(payload: any, rootId: string): Promise<any> {
    const result = await this.customApi.UpdateGraph(payload, { rootId: { eq: rootId } });
    if (result && this._graphsLoaded) {
      const idx = this._graphs.findIndex(g => g.id === payload.id);
      if (idx > -1) {
        this._graphs[idx] = result;
      }
    }
    return result;
  }

  // Delete a graph (scoped to its root) and drop it from the cache.
  async deleteGraph(graphId: string, rootId: string): Promise<any> {
    try {
      const result = await this.customApi.DeleteGraph({ id: graphId }, { rootId: { eq: rootId } });
      if (this._graphsLoaded) {
        this._graphs = this._graphs.filter(g => g.id !== graphId);
      }
      return result;
    } catch (e) {
      console.log('Error deleting graph', e);
      return null;
    }
  }

  // In-flight de-dup: parallel callers for the same root id share one GetEntity request.
  private _rootEntityInFlight: { [entityId: string]: Promise<GetEntityQuery | null> } = {};

  async getRootEntityById(entityId: string): Promise<GetEntityQuery | null> {
    if (this._entityId === entityId && this._entity) {
      return this._entity;
    }
    if (this._rootEntityInFlight[entityId]) {
      return this._rootEntityInFlight[entityId];
    }
    this._rootEntityInFlight[entityId] = (async () => {
      try {
        const entity = await this.customApi.GetEntity(entityId);
        if (entity && entity.entityType !== EntityTypeEnum.CHILD_ENTITY) {
          this._entityId = entityId;
          this._entity = entity;
        }
        return entity;
      } catch (e) {
        console.log('Cannot get the entity ', e);
        // A 401 means the caller's session/permissions were rejected fetching this entity — send every
        // consumer of getRootEntityById to the same unauthorized route instead of leaving each of them
        // stuck with a silently null entity.
        if (e?.errors?.[0]?.message === 'Unauthorized') {
          this.router.navigate(['/not-authorized']);
        }
        return null;
      } finally {
        delete this._rootEntityInFlight[entityId];
      }
    })();
    return this._rootEntityInFlight[entityId];
  }

  // Register (create), refresh, or patch a sub-entity in the root cache. This is the single entry point
  // for keeping _assessmentMap / _entity consistent without a DB round-trip.
  //  • Full entity (has activeAssessmentId + frameworks): registers a new AssessmentEntry, or MERGES into
  //    an existing one — never wiping already-loaded scores / trees / questions / risks.
  //  • Partial patch (e.g. { id, spiderChartSetting }): shallow-merges the fields into the cached root
  //    entity and/or the matching sub-entity entry (replaces the former updateCachedEntity()).
  addOrUpdateSubEntityInCache(entity: any): void {
    if (!entity?.id) {
      return;
    }

    // Keep the cached root entity fresh when the patch targets it.
    if (this._entity?.id === entity.id) {
      this._entity = { ...this._entity, ...entity };
      return;
    }

    const assessmentId = entity.activeAssessmentId || entity.activeAssessment?.id;

    // Partial patch (no assessment info) — shallow-merge into the matching sub-entity entry by id.
    if (!assessmentId) {
      const patchEntry = Object.values(this._assessmentMap).find(e => e.subEntity?.id === entity.id);
      if (patchEntry) {
        patchEntry.subEntity = { ...patchEntry.subEntity, ...entity };
      }
      return;
    }

    // _assessmentMap is a per-root cache (reset on root switch) and getSubEntities() returns every entry
    // in it, so only register entities that belong to the currently-loaded root entity.
    const rootId = entity.rootEntityId || entity.parentId;
    if (!this._entityId || (rootId && rootId !== this._entityId)) {
      return;
    }

    const frameworks = entity.activeAssessment?.standardFrameworkList?.items || [];
    const subEntity: any = { ...entity, logoUrl: entity.logoUrl || null };

    const existing = this._assessmentMap[assessmentId];
    if (existing) {
      // Merge — preserve already-loaded scores / trees / questions / risks.
      // Same preservation as `existing.assessment` below: subscription/partial payloads carry
      // activeAssessment: null — never let it wipe the hydrated copy on subEntity.
      const prevActiveAssessment = existing.subEntity?.activeAssessment;
      const prevVendorDetails = existing.subEntity?.vendorDetails;
      existing.subEntity = { ...existing.subEntity, ...subEntity };
      if (!entity.activeAssessment?.standardFrameworkList?.items?.length && prevActiveAssessment) {
        existing.subEntity.activeAssessment = prevActiveAssessment;
      }
      // Same reasoning for the vendorDetails @hasOne — a partial patch (e.g. a project-manager
      // updateEntity) carries vendorDetails: null; never let it wipe the hydrated copy.
      if (subEntity.vendorDetails == null && existing.subEntity && prevVendorDetails) {
        existing.subEntity.vendorDetails = prevVendorDetails;
      }
      existing.childEntityId = entity.id;
      existing.subEntityName = entity.name ?? existing.subEntityName;
      // Preserve the already-hydrated framework list when the incoming activeAssessment doesn't carry its
      // standardFrameworkList.items (e.g. entity subscription deltas), so getAssessment stays cache-served.
      if (entity.activeAssessment) {
        const incomingList = entity.activeAssessment.standardFrameworkList;
        const hasItems = incomingList?.items?.length;
        existing.assessment = {
          ...existing.assessment,
          ...entity.activeAssessment,
          standardFrameworkList: hasItems ? incomingList : existing.assessment?.standardFrameworkList,
        };
      }
      // (Re)build the frameworks map only when frameworks were provided and none are cached yet.
      if (frameworks.length && !Object.keys(existing.frameworks || {}).length) {
        existing.frameworks = this.populateFrameworksForAssessment(assessmentId, frameworks, entity.activeAssessment);
      }
    } else {
      this._assessmentMap[assessmentId] = {
        subEntity,
        childEntityId: entity.id,
        subEntityName: entity.name,
        frameworks: this.populateFrameworksForAssessment(assessmentId, frameworks, entity.activeAssessment),
        scoresLoaded: false,
        questionsDbLoaded: false,
        questionDbData: [],
        assessment: entity.activeAssessment || null,
        risks: [],
        risksLoaded: false,
      };
    }
    this._subEntityToAssessment[entity.id] = assessmentId;
  }

  /**
   * Remove a sub-entity (and its assessment entry) from the cache after a hard delete.
   * There is no OnDeleteEntity subscription, so callers must invoke this explicitly, otherwise the
   * deleted entity keeps surfacing from getSubEntities() / getSubEntity() via the persisted cache.
   */
  removeSubEntityFromCache(entityId: string): void {
    if (!entityId) {
      return;
    }
    // Resolve the assessmentId via the reverse map, falling back to a scan by sub-entity id.
    const assessmentId =
      this._subEntityToAssessment[entityId] ||
      Object.keys(this._assessmentMap).find(id => this._assessmentMap[id]?.subEntity?.id === entityId);
    if (assessmentId) {
      delete this._assessmentMap[assessmentId];
    }
    delete this._subEntityToAssessment[entityId];
  }

  async getSubEntity(entityId: string): Promise<GetEntityQuery | null> {
    try {
      if (this._entityId === entityId && this._entity) {
        return this._entity;
      }
      // Look for the entity in the _assessmentMap (already loaded sub-entities) first.
      const subEntityEntry = Object.values(this._assessmentMap).find(entry => entry.subEntity?.id === entityId);
      if (subEntityEntry) {
        return subEntityEntry.subEntity;
      }
      const entity = await this.customApi.GetEntity(entityId);
      if (entity && entity.activeAssessmentId) {
        this._subEntityToAssessment[entity.id] = entity.activeAssessmentId;
        if (this._assessmentMap[entity.activeAssessmentId]) {
          this._assessmentMap[entity.activeAssessmentId].subEntity = entity;
        } else {
          this.populateAssessmentMapperForEntity([entity]);
        }
      }
      return entity ?? null;
    } catch (e) {
      console.log('Cannot get entity', e);
      return null;
    }
  }

  // Batch entity lookup by ids, filtered to the current domain (mirrors EntityService.getEntitiesByGivenIds).
  async getEntitiesByGivenIds(entityIds: string[]): Promise<GetEntityQuery[]> {
    try {
      if (entityIds && entityIds.length) {
        const results = await Promise.all(entityIds.map(id => this.getSubEntity(id)));
        const domain = DomainFrameworkService.getDomain();
        return results.filter(entity => entity?.domain === domain);
      }
    } catch (e) {
      console.log('Error fetching entities by ids:', e);
    }
    return [];
  }

  // Getter for getting the frameworkScores by AssessmentId and frameworkKey

  // Creates a tag via API and updates the local cache incrementally.
  async createTag(tagPayload: any): Promise<any> {
    try {
      const result = await this.customApi.CreateTags(tagPayload);
      if (result) {
        this._tagsStore.all.push(result);
        if (result.type) {
          if (!this._tagsStore.byType[result.type]) {
            this._tagsStore.byType[result.type] = [];
          }
          this._tagsStore.byType[result.type].push(result);
        }
        if (result.targetId) {
          if (!this._tagsStore.byTargetId[result.targetId]) {
            this._tagsStore.byTargetId[result.targetId] = [];
          }
          this._tagsStore.byTargetId[result.targetId].push(result);
        }
        if (result.parentTagId) {
          if (!this._tagsStore.byParentTagId[result.parentTagId]) {
            this._tagsStore.byParentTagId[result.parentTagId] = [];
          }
          this._tagsStore.byParentTagId[result.parentTagId].push(result);
        }
        const aId = result.assessmentFrameworkKey?.split('#')[0];
        const fKey = result.assessmentFrameworkKey?.split('#')[1];
        if (aId && fKey) {
          if (!this._tagsStore.byAssessmentId[aId]) {
            this._tagsStore.byAssessmentId[aId] = {};
          }
          if (!this._tagsStore.byAssessmentId[aId][fKey]) {
            this._tagsStore.byAssessmentId[aId][fKey] = [];
          }
          this._tagsStore.byAssessmentId[aId][fKey].push(result);
        }
      }
      return result;
    } catch (e) {
      console.log('Error creating tag', e);
      return null;
    }
  }

  // Updates a tag name via API, then keeps the cache coherent by renaming the parent tag AND every child
  // (parentTagId === tagId) in the source-of-truth `all`, and rebuilding every index via populateTagsStore().
  // This mirrors the dynamicLabelHandler lambda (which renames all children by parentTagId in the DB) and
  // guarantees byType / byTargetId / byParentTagId / byAssessmentId all reflect the new name.
  async updateTag(tagId: string, name: string, entityId: string): Promise<any> {
    try {
      const result = await this.customApi.UpdateTags({ id: tagId, name }, { entityId: { eq: entityId } });
      if (result) {
        this._tagsStore.all.forEach(tag => {
          if (tag.id === tagId || tag.parentTagId === tagId) {
            tag.name = name;
          }
        });
        // Rebuild every index from `all` so no stale copy in any index keeps the old name.
        this.populateTagsStore();
      }
      return result;
    } catch (e) {
      console.log('Error updating tag', e);
      return null;
    }
  }

  // Updates a category name via API, then renames the parent category AND every child sub-category
  // (categoryId === categoryId) in `all`, rebuilding every index via populateCategoriesStore() so byType /
  // byTargetId / byCategoryId / byAssessmentId all reflect the new name (mirrors the dynamicLabelHandler lambda).
  async updateCategory(categoryId: string, name: string, entityId: string): Promise<any> {
    try {
      const result = await this.customApi.UpdateCategory({ id: categoryId, name }, { entityId: { eq: entityId } });
      if (result) {
        this._categoriesStore.all.forEach(category => {
          if (category.id === categoryId || category.categoryId === categoryId) {
            category.name = name;
          }
        });
        // Rebuild every index from `all` so no stale copy in any index keeps the old name.
        this.populateCategoriesStore();
      }
      return result;
    } catch (e) {
      console.log('Error updating category', e);
      return null;
    }
  }

  // Deletes a tag via API, then drops the parent tag AND every child (parentTagId === tagId) from `all`
  // and rebuilds every index via populateTagsStore() so no stale ref lingers in any index
  // (mirrors the dynamicLabelHandler lambda cascading child deletions by parentTagId).
  async deleteTag(tagId: string, entityId: string): Promise<any> {
    try {
      const result = await this.customApi.DeleteTags({ id: tagId }, { entityId: { eq: entityId } });
      if (result) {
        this._tagsStore.all = this._tagsStore.all.filter((t: any) => t.id !== tagId && t.parentTagId !== tagId);
        this.populateTagsStore();
      }
      return result;
    } catch (e) {
      console.log('Error deleting tag', e);
      return null;
    }
  }

  // Deletes a category via API, then drops the parent category AND every child (categoryId === categoryId)
  // from `all` and rebuilds every index via populateCategoriesStore().
  async deleteCategory(categoryId: string, entityId: string): Promise<any> {
    try {
      const result = await this.customApi.DeleteCategory({ id: categoryId }, { entityId: { eq: entityId } });
      if (result) {
        this._categoriesStore.all = this._categoriesStore.all.filter(
          (c: any) => c.id !== categoryId && c.categoryId !== categoryId
        );
        this.populateCategoriesStore();
      }
      return result;
    } catch (e) {
      console.log('Error deleting category', e);
      return null;
    }
  }

  // Removes a tag from all cache indices (mirrors upsertTagInCache but for deletion)
  removeTagFromCache(tag: any, targetId: string): void {
    if (!tag?.id) {
      return;
    }

    if (!tag.parentTagId) {
      const findParentTag = this._tagsStore.all.find(t => t.id === tag.id);
      if (findParentTag) {
        tag.parentTagId = findParentTag.parentTagId;
      }
    }
    // Remove from all
    this._tagsStore.all = this._tagsStore.all.filter(t => t.id !== tag.id);
    // Remove from byType
    if (tag.type && this._tagsStore.byType[tag.type]) {
      this._tagsStore.byType[tag.type] = this._tagsStore.byType[tag.type].filter(t => t.id !== tag.id);
    }
    // Remove from byTargetId
    const effectiveTargetId = tag.targetId || targetId;
    if (effectiveTargetId && this._tagsStore.byTargetId[effectiveTargetId]) {
      this._tagsStore.byTargetId[effectiveTargetId] = this._tagsStore.byTargetId[effectiveTargetId].filter(
        t => t.id !== tag.id
      );
      // Clear loadedKeys so next access forces fresh fetch from backend
      this._tagsStore.loadedKeys.delete(`target|${effectiveTargetId}`);
    }
    // Remove from byParentTagId
    if (tag.parentTagId && this._tagsStore.byParentTagId[tag.parentTagId]) {
      this._tagsStore.byParentTagId[tag.parentTagId] = this._tagsStore.byParentTagId[tag.parentTagId].filter(
        t => t.id !== tag.id
      );
    }
  }

  // Removes a category from all cache indices (mirrors upsertCategoryInCache but for deletion)
  removeCategoryFromCache(category: any, targetId: string): void {
    if (!category?.id) {
      return;
    }

    if (!category.categoryId) {
      const findParentCategory = this._categoriesStore.all.find(c => c.id === category.id);
      if (findParentCategory) {
        category.categoryId = findParentCategory.categoryId;
      }
    }
    // Remove from all
    this._categoriesStore.all = this._categoriesStore.all.filter(c => c.id !== category.id);
    // Remove from byType
    if (category.type && this._categoriesStore.byType[category.type]) {
      this._categoriesStore.byType[category.type] = this._categoriesStore.byType[category.type].filter(
        c => c.id !== category.id
      );
    }
    // Remove from byTargetId and clear loadedKeys so next fetch re-populates from backend
    const effectiveTargetId = category.targetId || targetId;
    if (effectiveTargetId && this._categoriesStore.byTargetId[effectiveTargetId]) {
      this._categoriesStore.byTargetId[effectiveTargetId] = this._categoriesStore.byTargetId[effectiveTargetId].filter(
        c => c.id !== category.id
      );
      // Clear loadedKeys so next access forces fresh fetch from backend
      this._categoriesStore.loadedKeys.delete(`target|${effectiveTargetId}`);
    }
    // Remove from byCategoryId
    if (category.categoryId && this._categoriesStore.byCategoryId[category.categoryId]) {
      this._categoriesStore.byCategoryId[category.categoryId] = this._categoriesStore.byCategoryId[
        category.categoryId
      ].filter(c => c.id !== category.id);
    }
  }

  // Creates a category via API and updates the local cache incrementally.
  async createCategory(categoryPayload: any): Promise<any> {
    try {
      const result = await this.customApi.CreateCategory(categoryPayload);
      if (result) {
        this._categoriesStore.all.push(result);
        if (result.type) {
          if (!this._categoriesStore.byType[result.type]) {
            this._categoriesStore.byType[result.type] = [];
          }
          this._categoriesStore.byType[result.type].push(result);
        }
        if (result.targetId) {
          if (!this._categoriesStore.byTargetId[result.targetId]) {
            this._categoriesStore.byTargetId[result.targetId] = [];
          }
          this._categoriesStore.byTargetId[result.targetId].push(result);
        }
        if (result.categoryId) {
          if (!this._categoriesStore.byCategoryId[result.categoryId]) {
            this._categoriesStore.byCategoryId[result.categoryId] = [];
          }
          this._categoriesStore.byCategoryId[result.categoryId].push(result);
        }
        const aId = result.assessmentFrameworkKey?.split('#')[0];
        const fKey = result.assessmentFrameworkKey?.split('#')[1];
        if (aId && fKey) {
          if (!this._categoriesStore.byAssessmentId[aId]) {
            this._categoriesStore.byAssessmentId[aId] = {};
          }
          if (!this._categoriesStore.byAssessmentId[aId][fKey]) {
            this._categoriesStore.byAssessmentId[aId][fKey] = [];
          }
          this._categoriesStore.byAssessmentId[aId][fKey].push(result);
        }
      }
      return result;
    } catch (e) {
      console.log('Error creating category', e);
      return null;
    }
  }

  // Push a pre-built, already-persisted tag row into the store indices (used by the bulk label save).
  upsertTagInCache(tag: any): void {
    if (!tag?.id || this._tagsStore.all.some(t => t.id === tag.id)) {
      return;
    }
    this._tagsStore.all.push(tag);
    if (tag.type) {
      if (!this._tagsStore.byType[tag.type]) {
        this._tagsStore.byType[tag.type] = [];
      }
      this._tagsStore.byType[tag.type].push(tag);
    }
    if (tag.targetId) {
      if (!this._tagsStore.byTargetId[tag.targetId]) {
        this._tagsStore.byTargetId[tag.targetId] = [];
      }
      this._tagsStore.byTargetId[tag.targetId].push(tag);
    }
    if (tag.parentTagId) {
      if (!this._tagsStore.byParentTagId[tag.parentTagId]) {
        this._tagsStore.byParentTagId[tag.parentTagId] = [];
      }
      this._tagsStore.byParentTagId[tag.parentTagId].push(tag);
    }
  }

  // Push a pre-built, already-persisted category row into the store indices (used by the bulk label save).
  upsertCategoryInCache(category: any): void {
    if (!category?.id || this._categoriesStore.all.some(c => c.id === category.id)) {
      return;
    }
    this._categoriesStore.all.push(category);
    if (category.type) {
      if (!this._categoriesStore.byType[category.type]) {
        this._categoriesStore.byType[category.type] = [];
      }
      this._categoriesStore.byType[category.type].push(category);
    }
    if (category.targetId) {
      if (!this._categoriesStore.byTargetId[category.targetId]) {
        this._categoriesStore.byTargetId[category.targetId] = [];
      }
      this._categoriesStore.byTargetId[category.targetId].push(category);
    }
    if (category.categoryId) {
      if (!this._categoriesStore.byCategoryId[category.categoryId]) {
        this._categoriesStore.byCategoryId[category.categoryId] = [];
      }
      this._categoriesStore.byCategoryId[category.categoryId].push(category);
    }
  }

  private populateTagsStore(): void {
    const uniqueById = new Map<string, any>();
    this._tagsStore.all.forEach(tag => {
      if (tag?.id) {
        uniqueById.set(tag.id, tag);
      }
    });
    this._tagsStore.all = Array.from(uniqueById.values());
    this._tagsStore.byType = {};
    this._tagsStore.byTargetId = {};
    this._tagsStore.byParentTagId = {};
    this._tagsStore.byAssessmentId = {};

    this._tagsStore.all.forEach(tag => {
      // Populate byType
      if (tag.type) {
        if (!this._tagsStore.byType[tag.type]) {
          this._tagsStore.byType[tag.type] = [];
        }
        this._tagsStore.byType[tag.type].push(tag);
      }

      // Populate byTargetId
      if (tag.targetId) {
        if (!this._tagsStore.byTargetId[tag.targetId]) {
          this._tagsStore.byTargetId[tag.targetId] = [];
        }
        this._tagsStore.byTargetId[tag.targetId].push(tag);
      }
      // Populate byParentTagId
      if (tag.parentTagId) {
        if (!this._tagsStore.byParentTagId[tag.parentTagId]) {
          this._tagsStore.byParentTagId[tag.parentTagId] = [];
        }
        this._tagsStore.byParentTagId[tag.parentTagId].push(tag);
      }
      // Populate byAssessmentId and frameworkKey
      const assessmentId = tag?.assessmentFrameworkKey?.split('#')[0];
      const frameworkKey = tag?.assessmentFrameworkKey?.split('#')[1];
      if (assessmentId && frameworkKey) {
        if (!this._tagsStore.byAssessmentId[assessmentId]) {
          this._tagsStore.byAssessmentId[assessmentId] = {};
        }
        if (!this._tagsStore.byAssessmentId[assessmentId][frameworkKey]) {
          this._tagsStore.byAssessmentId[assessmentId][frameworkKey] = [];
        }
        this._tagsStore.byAssessmentId[assessmentId][frameworkKey].push(tag);
      }
    });
  }

  private populateCategoriesStore(): void {
    const uniqueById = new Map<string, any>();
    this._categoriesStore.all.forEach(category => {
      if (category?.id) {
        uniqueById.set(category.id, category);
      }
    });
    this._categoriesStore.all = Array.from(uniqueById.values());
    this._categoriesStore.byType = {};
    this._categoriesStore.byTargetId = {};
    this._categoriesStore.byCategoryId = {};
    this._categoriesStore.byAssessmentId = {};

    this._categoriesStore.all.forEach(category => {
      // Populate byType
      if (category.type) {
        if (!this._categoriesStore.byType[category.type]) {
          this._categoriesStore.byType[category.type] = [];
        }
        this._categoriesStore.byType[category.type].push(category);
      }

      // Populate byTargetId
      if (category.targetId) {
        if (!this._categoriesStore.byTargetId[category.targetId]) {
          this._categoriesStore.byTargetId[category.targetId] = [];
        }
        this._categoriesStore.byTargetId[category.targetId].push(category);
      }
      // Populate byCategoryId
      if (category.categoryId) {
        if (!this._categoriesStore.byCategoryId[category.categoryId]) {
          this._categoriesStore.byCategoryId[category.categoryId] = [];
        }
        this._categoriesStore.byCategoryId[category.categoryId].push(category);
      }
      // Populate byAssessmentId and frameworkKey
      const assessmentId = category?.assessmentFrameworkKey?.split('#')[0];
      const frameworkKey = category?.assessmentFrameworkKey?.split('#')[1];
      if (assessmentId && frameworkKey) {
        if (!this._categoriesStore.byAssessmentId[assessmentId]) {
          this._categoriesStore.byAssessmentId[assessmentId] = {};
        }
        if (!this._categoriesStore.byAssessmentId[assessmentId][frameworkKey]) {
          this._categoriesStore.byAssessmentId[assessmentId][frameworkKey] = [];
        }
        this._categoriesStore.byAssessmentId[assessmentId][frameworkKey].push(category);
      }
    });
  }

  private async populateTagsByEntityId(entityId: string, type?: LabelType, targetId?: string): Promise<void> {
    try {
      const limit = 5000;
      let nextToken;
      let filter: any = null;
      if (type) {
        filter = {
          type: {
            eq: type,
          },
        };
      }
      if (targetId) {
        filter.targetId = {
          eq: targetId,
        };
      }
      do {
        const { items: tags, nextToken: rnextToken }: TagsByEntityIdQuery = await this.customApi.TagsByEntityId(
          entityId,
          null,
          filter,
          limit,
          nextToken
        );
        nextToken = rnextToken;
        this._tagsStore.all = this._tagsStore.all.concat(tags);
      } while (nextToken);
      // Calling helper function to populate the tags store with the fetched tags
      this.populateTagsStore();
      this._tagsStore.loadedKeys.add(`${type ?? 'all'}|${targetId ?? 'all'}`);
    } catch (error) {
      console.log('cannot get the tags error - ', error);
    }
  }

  private async populateCategoriesByEntityId(entityId: string, type?: LabelType, targetId?: string): Promise<void> {
    try {
      const limit = 5000;
      let nextToken;
      let filter: any = {
        type: {
          eq: type,
        },
      };
      if (targetId) {
        filter.targetId = {
          eq: targetId,
        };
      }
      do {
        const { items: categories, nextToken: rnextToken } = await this.customApi.CategoriesByEntity(
          entityId,
          null,
          filter,
          limit,
          nextToken
        );
        nextToken = rnextToken;
        this._categoriesStore.all = this._categoriesStore.all.concat(categories);
      } while (nextToken);
      // Calling helper function to populate the categories store with the fetched categories
      this.populateCategoriesStore();
      this._categoriesStore.loadedKeys.add(`${type ?? 'all'}|${targetId ?? 'all'}`);
    } catch (error) {
      console.log('cannot get the categories error - ', error);
    }
  }

  private async populateTagsByTargetId(targetId: string, filter: any = null): Promise<void> {
    try {
      filter = filter ?? (this._entityId ? { entityId: { eq: this._entityId } } : null);
      const tags = await this.customApi.TagsByTargetId(targetId, undefined, filter, 10000, undefined);
      this._tagsStore.all = this._tagsStore.all.concat(tags?.items || []);
      this.populateTagsStore();
      this._tagsStore.loadedKeys.add(`target|${targetId}`);
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  private async populateCategoriesByTargetId(targetId: string, filter: any = null): Promise<void> {
    try {
      filter = filter ?? (this._entityId ? { entityId: { eq: this._entityId } } : null);
      const categories = await this.customApi.CategoriesByTargetId(targetId, undefined, filter, 10000, undefined);
      this._categoriesStore.all = this._categoriesStore.all.concat(categories?.items || []);
      this.populateCategoriesStore();
      this._categoriesStore.loadedKeys.add(`target|${targetId}`);
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  private async populateTagsByParentTagId(parentTagId: string, filter: any = null): Promise<void> {
    try {
      const effectiveFilter = filter ?? (this._entityId ? { entityId: { eq: this._entityId } } : null);
      const tags = await this.customApi.TagByParentTagId(parentTagId, undefined, effectiveFilter, 10000, undefined);
      this._tagsStore.all = this._tagsStore.all.concat(tags?.items || []);
      this.populateTagsStore();
      this._tagsStore.loadedKeys.add(`parent|${parentTagId}`);
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  private async populateCategoriesByCategoryId(categoryId: string, filter: any = null): Promise<void> {
    try {
      const effectiveFilter = filter ?? (this._entityId ? { entityId: { eq: this._entityId } } : null);
      const categories = await this.customApi.CategoriesByCategoryId(
        categoryId,
        undefined,
        effectiveFilter,
        10000,
        undefined
      );
      this._categoriesStore.all = this._categoriesStore.all.concat(categories?.items || []);
      this.populateCategoriesStore();
      this._categoriesStore.loadedKeys.add(`category|${categoryId}`);
    } catch (err: any) {
      console.log('Error: ', err);
      return Promise.reject(err);
    }
  }

  private async loadTagsByAssessmentFrameworkKey(assessmentId: string, frameworkKey: string): Promise<any> {
    try {
      const limit = 5000;
      let nextToken;
      let assessmentFrameworkKeyTags = [];
      do {
        const { items: tags, nextToken: rnextToken } = await this.customApi.TagsByAssessmentFrameworkKey(
          `${assessmentId}#${frameworkKey}`,
          null,
          null,
          limit,
          nextToken
        );
        nextToken = rnextToken;
        if (tags?.length) {
          assessmentFrameworkKeyTags = [...assessmentFrameworkKeyTags, ...tags];
        }
      } while (nextToken);
      assessmentFrameworkKeyTags = assessmentFrameworkKeyTags?.sort((a, b) => a.name.localeCompare(b.name)) || [];
      this._tagsStore.all = this._tagsStore.all.concat(assessmentFrameworkKeyTags);
      // Populating the tags for the assessment framework key in the local _tagsStore to avoid unnecessary
      // API calls in future when user tries to access the same assessment framework key's tags again.
      this.populateTagsStore();
      // Sentinel: guarantee the key exists even when API returned 0 items so the cache guard won't re-fetch.
      if (!this._tagsStore.byAssessmentId[assessmentId]) {
        this._tagsStore.byAssessmentId[assessmentId] = {};
      }
      if (!this._tagsStore.byAssessmentId[assessmentId][frameworkKey]) {
        this._tagsStore.byAssessmentId[assessmentId][frameworkKey] = [];
      }
    } catch (error) {
      console.log('cannot get the tags error - ', error);
      return [];
    }
  }

  private async loadCategoriesByAssessmentFrameworkKey(assessmentId: string, frameworkKey: string): Promise<any> {
    try {
      const limit = 5000;
      let nextToken;
      let allCategories = [];
      do {
        const { items: categories, nextToken: rnextToken } = await this.customApi.CategoriesByAssessmentFrameworkKey(
          `${assessmentId}#${frameworkKey}`,
          null,
          null,
          limit,
          nextToken
        );
        nextToken = rnextToken;
        if (categories?.length) {
          allCategories = [...allCategories, ...categories];
        }
      } while (nextToken);
      allCategories = allCategories?.sort((a, b) => a.name.localeCompare(b.name)) || [];
      this._categoriesStore.all = this._categoriesStore.all.concat(allCategories);
      // Populating the categories for the assessment framework key in the local _categoriesStore to avoid unnecessary
      // API calls in future when user tries to access the same assessment framework key's categories again.
      this.populateCategoriesStore();
      // Sentinel: guarantee the key exists even when API returned 0 items so the cache guard won't re-fetch.
      if (!this._categoriesStore.byAssessmentId[assessmentId]) {
        this._categoriesStore.byAssessmentId[assessmentId] = {};
      }
      if (!this._categoriesStore.byAssessmentId[assessmentId][frameworkKey]) {
        this._categoriesStore.byAssessmentId[assessmentId][frameworkKey] = [];
      }
    } catch (error) {
      console.log('cannot get the categories error - ', error);
      return [];
    }
  }

  private getVisibleAssessmentStandardFrameworkFilter(): ModelAssessmentStandardFrameworkFilterInput {
    return { not: { not_added: { eq: true } } };
  }

  private populateFrameworksForAssessment(assessmentId: string, frameworks: any[], parentAssessment?: any): any {
    const frameworksMapper = {};
    const assessmentMeta = parentAssessment?.childId
      ? { id: parentAssessment.id || assessmentId, rootId: parentAssessment.rootId, childId: parentAssessment.childId }
      : null;
    const existingFrameworks = this._assessmentMap[assessmentId]?.frameworks;
    frameworks.forEach(asf => {
      const asfWithAssessment =
        assessmentMeta && !asf.assessment?.childId
          ? { ...asf, assessment: { ...(asf.assessment || {}), ...assessmentMeta } }
          : asf;
      frameworksMapper[asf.key] = {
        asf: asfWithAssessment,
        score: existingFrameworks?.[asf.key]?.score || null,
        tree: existingFrameworks?.[asf.key]?.tree || null,
        groupedTree: existingFrameworks?.[asf.key]?.groupedTree || null,
        listQuestions: existingFrameworks?.[asf.key]?.listQuestions || [],
        loadedQuestionIds: existingFrameworks?.[asf.key]?.loadedQuestionIds || new Set<string>(),
        metadata: existingFrameworks?.[asf.key]?.metadata || {
          functions: new Set<string>(),
          controls: new Set<string>(),
          domains: new Set<string>(),
        },
        treeLoaded: existingFrameworks?.[asf.key]?.treeLoaded || false,
        groupedTreeLoaded: existingFrameworks?.[asf.key]?.groupedTreeLoaded || false,
        scoreLoaded: existingFrameworks?.[asf.key]?.scoreLoaded || false,
      };
      frameworksMapper[asf.key].asf.name = this.domainFrameworkService.getFrameworkName(asf.key.split('#')[0]);
    });
    return frameworksMapper;
  }

  private populateAssessmentMapperForEntity(entityList: any): void {
    // Populate the local _assessmentMapper.
    entityList.forEach(entity => {
      const assessmentId = entity?.activeAssessmentId || entity?.activeAssessment?.id;
      if (assessmentId && !this._assessmentMap[assessmentId]) {
        this._assessmentMap[assessmentId] = {
          subEntity: { ...entity, logoUrl: entity.logoUrl || null },
          childEntityId: entity.id,
          subEntityName: entity.name,
          frameworks: this.populateFrameworksForAssessment(
            assessmentId,
            entity.activeAssessment?.standardFrameworkList?.items || [],
            entity.activeAssessment
          ),
          scoresLoaded: false,
          questionsDbLoaded: false,
          questionDbData: [],
          assessment: entity.activeAssessment || null,
          risks: [],
          risksLoaded: false,
        };
      }
      if (!this._subEntityToAssessment[entity.id] && assessmentId) {
        this._subEntityToAssessment[entity.id] = assessmentId;
      }
    });
  }

  async listEntitiesByParentId(parentId: string, filter?: ModelEntityFilterInput): Promise<GetEntityQuery[]> {
    try {
      // Return from cache if already loaded for this parentId (an empty result still counts as loaded).
      if (this._subEntitiesLoadedByParent[parentId] && this._entityId === parentId) {
        return Object.values(this._assessmentMap).map(entry => entry.subEntity);
      }
      // In-flight deduplication: reuse an existing promise if one is already running.
      if (this._listEntitiesInFlight[parentId]) {
        return this._listEntitiesInFlight[parentId];
      }
      const fetch = (async () => {
        let entityList: GetEntityQuery[] = [];
        let nextToken = null;
        const frameworkFilter = this.getVisibleAssessmentStandardFrameworkFilter();
        const childFilter: ModelEntityFilterInput = {
          entityType: { eq: EntityTypeEnum.CHILD_ENTITY },
          domain: { eq: DomainFrameworkService.getDomain() },
        };
        filter = { ...childFilter, ...filter };
        do {
          const entityResponse: EntityParentQuery = await this.customApi.EntitiesByParent(
            parentId,
            null,
            filter,
            environment.queryListLimit,
            nextToken,
            frameworkFilter
          );
          const hydratedItems = await this.hydrateStandardFrameworkLists(
            entityResponse.items as GetEntityQuery[],
            frameworkFilter
          );
          entityList = entityList.concat(hydratedItems as any);
          nextToken = entityResponse.nextToken;
        } while (nextToken);
        let subEntities = entityList?.length ? entityList : [];
        // Need to add helper function here to get the logoUrl.
        subEntities = await this.getLogoUrlForEntities(subEntities);
        subEntities = await this.hydrateStandardFrameworkLists(subEntities, frameworkFilter);
        this.populateAssessmentMapperForEntity(subEntities as GetEntityQuery[]);
        this._entityId = this._entityId ?? parentId;
        // Mark as loaded even when the result is empty, so subsequent calls are served from cache.
        this._subEntitiesLoadedByParent[parentId] = true;
        void this._initSubscriptionsInternal();
        delete this._listEntitiesInFlight[parentId];
        return subEntities;
      })();
      this._listEntitiesInFlight[parentId] = fetch;
      return fetch;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  private async getLogoUrlForEntities(entities: GetEntityQuery[]): Promise<any> {
    try {
      const updatedList = await Promise.all(
        entities.map(async (ent: any) => {
          if (ent && (ent.logo || ent?.logoUrl)) {
            const newLogoUrl = await this.fileService.getPresignedS3DownloadUrl(ent?.logo?.key || ent.logoUrl);
            // Return a new object with the updated logoUrl (immutable pattern)
            return { ...ent, logoUrl: newLogoUrl };
          }
          return ent; // Return the original entity if no changes
        })
      );
      return updatedList;
    } catch (e) {
      console.log('Error fetching logo URLs for entities', e);
      return entities; // Return the original list if there's an error
    }
  }

  private async hydrateStandardFrameworkList(
    entity: GetEntityQuery,
    frameworkFilter?: ModelAssessmentStandardFrameworkFilterInput
  ): Promise<GetEntityQuery> {
    const standardFrameworkList = entity?.activeAssessment?.standardFrameworkList;
    const assessmentId = entity?.activeAssessmentId || entity?.activeAssessment?.id;

    if (!assessmentId || !standardFrameworkList?.nextToken) {
      return entity;
    }

    try {
      let nextToken = null;
      let frameworkList = [];

      do {
        const standardFrameworkResponse = await this.customApi.StandardFrameworksByAssessmentId(
          assessmentId,
          null,
          frameworkFilter,
          environment.queryListLimit,
          nextToken
        );

        frameworkList = frameworkList.concat(standardFrameworkResponse?.items || []);
        nextToken = standardFrameworkResponse?.nextToken || null;
      } while (nextToken);

      const uniqueItemsMap = new Map();
      frameworkList.forEach(item => {
        if (item?.id) {
          uniqueItemsMap.set(item.id, item);
        }
      });

      entity.activeAssessment.standardFrameworkList.items = Array.from(uniqueItemsMap.values());
      entity.activeAssessment.standardFrameworkList.nextToken = null;
    } catch (err) {
      console.log('Err:: Failed to hydrate assessment standard framework list', err);
    }

    return entity;
  }

  private async hydrateStandardFrameworkLists(
    entities: GetEntityQuery[],
    frameworkFilter?: ModelAssessmentStandardFrameworkFilterInput
  ): Promise<GetEntityQuery[]> {
    return await Promise.all(
      (entities || []).map(entity => this.hydrateStandardFrameworkList(entity, frameworkFilter))
    );
  }

  private async getASFListByAssessmentId(
    assessmentId: string,
    filter?: ModelAssessmentStandardFrameworkFilterInput
  ): Promise<any[]> {
    let asfList = [];
    let nextToken = null;
    do {
      const standardFrameworkResponse = await this.customApi.StandardFrameworksByAssessmentId(
        assessmentId,
        null,
        filter,
        environment.queryListLimit,
        nextToken
      );
      asfList = asfList.concat(standardFrameworkResponse?.items || []);
      nextToken = standardFrameworkResponse?.nextToken || null;
    } while (nextToken);

    // Populating the frameworks for the assessment in the local _assessmentMapper to
    // avoid unnecessary API calls in future when user tries to access the same assessment's frameworks again.
    if (this._assessmentMap[assessmentId]) {
      const entry = this._assessmentMap[assessmentId];
      const fresh = this.populateFrameworksForAssessment(assessmentId, asfList, entry.assessment);
      entry.frameworks = { ...fresh };
    }
    return asfList;
  }

  // Paginate all QuestionData for a single assessment. Pure fetch — no caching side effects.
  private async fetchQuestionsForAssessment(assessmentId: string): Promise<any[]> {
    let allQuestionDataInDb: any[] = [];
    let nextToken = null;
    do {
      const response = await this.customApi.QuestionsByAssessmentId(assessmentId, null, null, 10000, nextToken);
      nextToken = response.nextToken;
      allQuestionDataInDb = [...allQuestionDataInDb, ...(response?.items ?? [])];
    } while (nextToken);
    return allQuestionDataInDb;
  }

  // Add Helper function to fetch the list of questions from tree;
  flatTreeList(
    node: any,
    assessmentId: string,
    frameworkKey: string,
    entityName: string,
    allQuestionDataInDb: any,
    childEntityId: string
  ): void {
    try {
      if (!node) {
        return;
      } // tree can be null if read before an async backend process finishes writing it
      if (node?.children && node?.children?.length > 0) {
        node.children.forEach(child => {
          this.selectionPath.push(child.name);
          this.flatTreeList(child, assessmentId, frameworkKey, entityName, allQuestionDataInDb, childEntityId);
          if (this.selectionPath?.length) {
            this.selectionPath?.pop();
          }
        });
      } else if (node.questions) {
        node.questions = node.questions.map(question => {
          const questionData = allQuestionDataInDb.find(q => q.id === question.id);
          question.function = this.selectionPath[0];
          question.category = this.selectionPath[1];
          question.subCategory = this.selectionPath[2];
          question.framework = frameworkKey;
          question.control = `${node.name}, ${node.label}`;
          const notApplicable = this.NA_SCORES.includes(question?.statistic?.totalScore) || false;
          question.entity = entityName;
          question.score = question?.statistic?.totalScore != null ? question.statistic.totalScore : null;
          question.maturity = question?.statistic?.totalScore
            ? UtilsService.calculateMaturityScore(question?.statistic?.totalScore)
            : 0;
          question.isNotApplicable = notApplicable;
          question.assessmentId = assessmentId;
          question.childIds = [this._assessmentMap[assessmentId]?.childEntityId || childEntityId];
          // question.labels = ?.flat || [];
          // question.labelGroups = labelData?.grouped || [];
          // question.tags = question.labels.filter(l => l.source === LabelSourceType.TAG);
          // question.groups = [...new Set(this.getGroupsByTags(question.tags))];
          question.assessmentIds = [assessmentId];
          question.questionData = questionData;
          question.childEntityId = childEntityId;

          this._assessmentMap[assessmentId].frameworks[frameworkKey].listQuestions.push(question);
          return question;
        });
      }
    } catch (e) {
      console.log('error ================ ', e);
    }
  }
  /**
   * Shares one S3 request per identical (assessment, framework, flags) tuple: when a background
   * prefetch and an explicit Collection load ask for the same tree at the same time, the second
   * caller joins the first request instead of issuing its own. The key carries the flags because
   * they change what the fetch produces (grouped tree, flattened question list).
   */
  private getAssessmentTreeFromS3(
    assessmentId: string,
    framework: any,
    fetchGroupedTree = false,
    skipFlatten = false
  ): Promise<any> {
    const inFlightKey = `${assessmentId}#${framework?.key}#${fetchGroupedTree}#${skipFlatten}`;
    if (this._treeInFlight[inFlightKey]) {
      return this._treeInFlight[inFlightKey];
    }
    const fetchPromise = this.fetchAssessmentTreeFromS3(assessmentId, framework, fetchGroupedTree, skipFlatten).finally(
      () => {
        delete this._treeInFlight[inFlightKey];
      }
    );
    this._treeInFlight[inFlightKey] = fetchPromise;
    return fetchPromise;
  }

  private async fetchAssessmentTreeFromS3(
    assessmentId: string,
    framework: any,
    fetchGroupedTree = false,
    skipFlatten = false
  ): Promise<any> {
    try {
      const regularPath = `${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${framework.key}.json`;
      const cachedEntry = this._assessmentMap[assessmentId]?.frameworks[framework.key];

      // When fetching only the grouped tree and the regular tree is already cached,
      // skip the S3 fetch and resolve immediately from cache.
      const regularFetch =
        fetchGroupedTree && cachedEntry?.treeLoaded
          ? Promise.resolve(cachedEntry.tree)
          : this.fileService.getJSONFileFromS3(regularPath, {
              download: true,
              Recursive: true,
              contentType: 'application/json',
              level: 'public',
              cacheControl: 'no-cache',
            });

      const groupedFetch = fetchGroupedTree
        ? this.fileService
            .getJSONFileFromS3(`${FileTypeEnum.ASSESSMENTS}/${assessmentId}/${framework.key}/GROUPS/GROUPS.json`, {
              download: true,
              contentType: 'application/json',
              level: 'public',
              cacheControl: 'no-cache',
            })
            .catch(() => null)
        : Promise.resolve(null);

      const [result, groupedResult] = await Promise.all([regularFetch, groupedFetch]);

      // Populate the tree for the assessment framework in the local _assessmentMapper to avoid unnecessary
      // API calls in future when user tries to access the same assessment framework's tree again.
      if (this._assessmentMap[assessmentId] && this._assessmentMap[assessmentId].frameworks[framework.key]) {
        this._assessmentMap[assessmentId].frameworks[framework.key].tree = result;
        this._assessmentMap[assessmentId].frameworks[framework.key].treeLoaded = true;
        if (fetchGroupedTree) {
          this._assessmentMap[assessmentId].frameworks[framework.key].groupedTree = groupedResult;
          this._assessmentMap[assessmentId].frameworks[framework.key].groupedTreeLoaded = true;
        }
      } else if (
        this._assessmentMap[assessmentId] &&
        (framework?.name.toLowerCase() === AdditionalRequirements.REQUIREMENTS ||
          framework?.name.toLowerCase() === AdditionalRequirements.CERTIFICATIONS)
      ) {
        // Vendor combined cards (CERTIFICATIONS/REQUIREMENTS) merge several real ASFs into one virtual
        // key that was never registered by populateFrameworksForAssessment — create its entry here.
        if (!this._assessmentMap[assessmentId].frameworks) {
          this._assessmentMap[assessmentId].frameworks = {};
        }
        this._assessmentMap[assessmentId].frameworks[framework.key] = {
          asf: framework,
          score: null,
          tree: result,
          groupedTree: fetchGroupedTree ? groupedResult : null,
          listQuestions: [],
          loadedQuestionIds: new Set<string>(),
          metadata: {
            functions: new Set<string>(),
            controls: new Set<string>(),
            domains: new Set<string>(),
          },
          treeLoaded: true,
          groupedTreeLoaded: !!fetchGroupedTree,
          scoreLoaded: false,
        };
      }

      if (!fetchGroupedTree && !skipFlatten) {
        this.flatTreeList(
          result,
          assessmentId,
          framework.key,
          this._assessmentMap[assessmentId]?.subEntityName || '',
          this._assessmentMap[assessmentId]?.questionDbData || [],
          this._assessmentMap[assessmentId]?.childEntityId || ''
        );
      }
      return result;
    } catch (err) {
      console.log('ERR: getAssessmentTreeFromS3 - :', err);
      return null;
    }
  }

  /**
   * Keep the cached S3 trees in sync with a live FrameworkScores event.
   * The regular tree's first-level children are the framework FUNCTIONS and the grouped
   * tree's children are the GROUPS — both have `scores` baked in at S3-fetch time, so
   * without this patch re-opening a framework serves pre-answer scores until a reload.
   * Only display-relevant score fields are copied; tree structure is untouched.
   */
  private syncTreeScoresFromFrameworkScore(fw: any, score: any): void {
    const copyScoreFields = (target: any, source: any): void => {
      if (!target || !source) {
        return;
      }
      ['collection', 'remediation', 'total', 'target', 'maturity', 'totalQuestions', 'completedQuestions'].forEach(
        field => {
          if (source[field] != null) {
            target[field] = source[field];
          }
        }
      );
      // Mirror updateLocalScores semantics — only patch reassessment when both sides track it.
      if (target.reassessment >= 0 && source.reassessment >= 0) {
        target.reassessment = source.reassessment;
      }
    };

    const collectNamedScores = (wrappers: any): { [name: string]: any } => {
      const byName: { [name: string]: any } = {};
      const entries = Array.isArray(wrappers) ? wrappers : Object.values(wrappers ?? {});
      entries.forEach((entry: any) => {
        if (entry && typeof entry === 'object') {
          Object.keys(entry).forEach(name => {
            // Only accept object values — skips scalar fields (e.g. top-level totals).
            if (entry[name] && typeof entry[name] === 'object') {
              byName[name.toLowerCase()] = entry[name];
            }
          });
        }
      });
      return byName;
    };

    const patchChildren = (children: any[], scoresByName: { [name: string]: any }): void => {
      (children || []).forEach((node: any) => {
        const nodeScores = node?.name ? scoresByName[node.name.toLowerCase()] : null;
        if (nodeScores) {
          copyScoreFields(node.scores, nodeScores);
        }
      });
    };

    // 1. Regular tree — top-level scores + first-level children (framework functions).
    if (fw.treeLoaded && fw.tree) {
      copyScoreFields(fw.tree.scores, score);
      if (Array.isArray(score.functions)) {
        patchChildren(fw.tree.children, collectNamedScores(score.functions));
      }
    }

    // 2. Grouped tree — top-level scores + group children.
    if (fw.groupedTreeLoaded && fw.groupedTree && score.groups && typeof score.groups === 'object') {
      copyScoreFields(fw.groupedTree.scores, score.groups);
      patchChildren(fw.groupedTree.children, collectNamedScores(score.groups));
    }
  }

  // ─── AppSync real-time subscriptions ────────────────────────────────────────

  /**
   * Internal bootstrap: fetches the auth token and calls initSubscriptions.
   * Guards against running before rootEntityId is known or when already active.
   */
  private async _initSubscriptionsInternal(): Promise<void> {
    if (this._entitySub || !this._entityId) {
      return;
    }
    try {
      const token = await this.authSessionCacheService.getAccessToken();
      if (!token) {
        return;
      }
      this.initSubscriptions(token, this._entityId);
    } catch {
      // Token not ready yet — will retry on next reconnect.
    }
  }

  /**
   * Start the four AppSync real-time subscriptions for the given root entity.
   *
   * Subscriptions update the in-memory cache automatically and emit on the
   * corresponding public Subject so any component can react without polling.
   *
   * Safe to call more than once — calling it again tears down the previous set
   * before re-subscribing (e.g. when the user switches root entities).
   *
   * @param token        Cognito / Lambda auth token
   * @param rootEntityId The root entity whose data should be tracked
   */
  initSubscriptions(token: string, rootEntityId: string): void {
    this.teardownSubscriptions();

    // 1. Entity updates — AppSync-filtered to children of this root entity.
    this._entitySub = this.customApi.OnUpdateEntityListener(token, { rootEntityId: { eq: rootEntityId } }).subscribe({
      next: (res: any) => {
        const entity = res?.data?.onUpdateEntity;
        if (!entity) {
          return;
        }
        // Keep the cached root-entity record fresh.
        if (entity.id === this._entityId) {
          this._entity = entity;
        }
        const subEntry = this._assessmentMap[entity.activeAssessmentId];
        if (subEntry) {
          const prevActiveAssessment = subEntry.subEntity?.activeAssessment;
          const prevVendorDetails = subEntry.subEntity?.vendorDetails;
          subEntry.subEntity = { ...subEntry.subEntity, ...entity };
          if (!entity.activeAssessment?.standardFrameworkList?.items?.length && prevActiveAssessment) {
            subEntry.subEntity.activeAssessment = prevActiveAssessment;
          }
          if (entity.vendorDetails == null && prevVendorDetails) {
            subEntry.subEntity.vendorDetails = prevVendorDetails;
          }
        }
        this.onEntityUpdated.next(entity);
      },
      error: (err: any) => console.error('[RootEntityContext] entity subscription error:', err),
    });

    // 1b. Root entity updates — AppSync-filtered to the root entity itself (id == rootEntityId).
    // Child entities have rootEntityId set; root entity has rootEntityId: null.
    // So we need a separate subscription for the root to be notified of its own score updates.
    this._rootEntitySub = this.customApi.OnUpdateEntityListener(token, { id: { eq: rootEntityId } }).subscribe({
      next: (res: any) => {
        const entity = res?.data?.onUpdateEntity;
        if (!entity || entity.id !== this._entityId) {
          return;
        }
        // Keep the cached root-entity record fresh when root entity itself updates.
        this._entity = entity;
        this.onEntityUpdated.next(entity);
      },
      error: (err: any) => console.error('[RootEntityContext] root entity subscription error:', err),
    });

    // 2. Assessment updates — AppSync-filtered to assessments under this root entity.
    this._assessmentSub = this.customApi.OnUpdateAssessmentListener(token, { rootId: { eq: rootEntityId } }).subscribe({
      next: (res: any) => {
        const assessment = res?.data?.onUpdateAssessment;
        if (!assessment?.id) {
          return;
        }
        // Patch the cached assessment entry if we have it loaded. AppSync does not re-resolve the
        // standardFrameworkList @hasMany for subscription deltas, so it can arrive empty — preserve
        // the already-hydrated framework list instead of clobbering it (otherwise getAssessment
        // would fall through to a network fetch on the next read).
        const entry = this._assessmentMap[assessment.id];
        if (entry) {
          const incomingList = assessment.standardFrameworkList;
          const hasItems = incomingList?.items?.length;
          entry.assessment = {
            ...entry.assessment,
            ...assessment,
            standardFrameworkList: hasItems ? incomingList : entry.assessment?.standardFrameworkList,
          };
        }
        this.onAssessmentUpdated.next(assessment);
      },
      error: (err: any) => console.error('[RootEntityContext] assessment subscription error:', err),
    });

    // 3. Framework score updates — no server-side rootId filter available;
    //    events for unrelated assessments are dropped client-side.
    this._frameworkScoreSub = this.customApi.OnUpdateFrameworkScoresListener(token).subscribe({
      next: (res: any) => {
        const score = res?.data?.onUpdateFrameworkScores;
        if (!score?.assessmentId) {
          return;
        }
        const entry = this._assessmentMap[score.assessmentId];
        if (!entry) {
          return;
        }
        // `type` holds the framework key for FrameworkScores records.
        // split the id of score to find the new exact framework key and update the score for that framework.
        // splitting should be done on _ this bases only first _.
        const frameworkKey = score.id.split('_').slice(1).join('_');
        const fw = entry.frameworks[frameworkKey];
        if (fw) {
          if (score.groups) {
            score.groups = JSON.parse(score.groups);
          }
          if (score.functions) {
            score.functions = JSON.parse(score.functions);
          }
          // A partial push can omit functions/groups — keep the cached value instead of nulling it.
          const prevScore = fw.score;
          fw.score = {
            ...score,
            groups: score.groups ?? prevScore?.groups,
            functions: score.functions ?? prevScore?.functions,
          };
          fw.scoreLoaded = true;
          this.syncTreeScoresFromFrameworkScore(fw, fw.score);
        }
        this.onFrameworkScoreUpdated.next(score);
      },
      error: (err: any) => console.error('[RootEntityContext] framework score subscription error:', err),
    });

    // 4. AssessmentStandardFramework updates — dropped client-side if the
    //    parent assessment is not in our map.
    this._asfSub = this.customApi.OnUpdateAssessmentStandardFrameworkListener(token).subscribe({
      next: (res: any) => {
        const asf = res?.data?.onUpdateAssessmentStandardFramework;
        if (!asf?.assessmentId || !asf?.key) {
          return;
        }
        // Cross-entity guard: _assessmentMap is a per-root cache, so an entry only exists for
        // assessments belonging to the currently-loaded root — events for other entities are dropped.
        const entry = this._assessmentMap[asf.assessmentId];
        if (!entry) {
          return;
        }

        const fw = entry.frameworks[asf.key];
        if (fw) {
          const prevAsf = fw.asf;
          fw.asf = { ...prevAsf, ...asf };
          if (asf.timeline == null && prevAsf?.timeline) {
            fw.asf.timeline = prevAsf.timeline;
          }
          if (asf.defaultQuestionSettings == null && prevAsf?.defaultQuestionSettings) {
            fw.asf.defaultQuestionSettings = prevAsf.defaultQuestionSettings;
          }
          // The lambda writes filterMetadata onto the ASF — mirror it into the metadata Sets.
          this.applyFilterMetadata(fw, asf.filterMetadata);
        }
        this.onASFUpdated.next(asf);
      },
      error: (err: any) => console.error('[RootEntityContext] ASF subscription error:', err),
    });

    // 4b. AssessmentStandardFramework CREATES — e.g. ESE / smart-mapping / new sub-entity onboarding
    //    adding a framework to an existing assessment. Dropped client-side if the parent assessment is
    //    not in our map (per-root cache), so data from another root entity can never leak in.
    this._asfCreateSub = this.customApi.OnCreateAssessmentStandardFrameworkListener(token).subscribe({
      next: (res: any) => {
        const asf = res?.data?.onCreateAssessmentStandardFramework;
        if (!asf?.assessmentId || !asf?.key) {
          return;
        }
        // Cross-entity guard: only assessments belonging to the currently-loaded root are cached here.
        const entry = this._assessmentMap[asf.assessmentId];
        if (!entry) {
          return;
        }
        // Skip if already present (e.g. optimistic addOrUpdateFrameworkInCache from the creating flow).
        if (!entry.frameworks?.[asf.key]) {
          this.addOrUpdateFrameworkInCache(asf.assessmentId, asf);
        }
        this.onASFCreated.next(asf);
      },
      error: (err: any) => console.error('[RootEntityContext] ASF create subscription error:', err),
    });

    // 5. Framework score CREATES — when a new sub-entity + ASF triggers CalcAssessmentScore, the
    //    resulting FrameworkScores records are CREATED (not updated). Capture them so the score is
    //    cached with its exact true value, avoiding a DB refetch. Dropped client-side if the parent
    //    assessment / framework is not in our map.
    this._frameworkScoreCreateSub = this.customApi.OnCreateFrameworkScoresListener(token).subscribe({
      next: (res: any) => {
        const score = res?.data?.onCreateFrameworkScores;
        if (!score?.assessmentId) {
          return;
        }
        const entry = this._assessmentMap[score.assessmentId];
        if (!entry) {
          return;
        }
        // id = "assessmentId_FRAMEWORK_KEY" — split on the first underscore only.
        const frameworkKey = score.id.split('_').slice(1).join('_');
        const fw = entry.frameworks[frameworkKey];
        if (fw) {
          if (score.groups) {
            score.groups = JSON.parse(score.groups);
          }
          if (score.functions) {
            score.functions = JSON.parse(score.functions);
          }
          fw.score = score;
          fw.scoreLoaded = true;
          this.syncTreeScoresFromFrameworkScore(fw, score);
        } else {
          // A newly-created sub-entity's ASF may not be cached yet, but the score row does not say whether
          // that framework is live or a `not_added` catalog placeholder — read the real ASF before caching.
          void this.registerFrameworkFromScore(score.assessmentId, frameworkKey, score);
        }
        this.onFrameworkScoreCreated.next(score);
      },
      error: (err: any) => console.error('[RootEntityContext] framework score create subscription error:', err),
    });

    // 6. Risk CREATEs — cache newly-created risks (backend- or client-originated) under this root.
    //    Patched only when that assessment's risks were already loaded (updateRiskInCache is a no-op otherwise).
    this._riskCreateSub = this.customApi.OnCreateRiskByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const risk = res?.data?.onCreateRiskByEntityId;
        if (!risk?.assessmentId || !risk?.id) {
          return;
        }
        this.updateRiskInCache(risk);
        this.onRiskUpdated.next({ type: 'created', risk });
      },
      error: (err: any) => console.error('[RootEntityContext] risk create subscription error:', err),
    });

    // 7. Risk UPDATEs — patch the cached risk in place.
    this._riskUpdateSub = this.customApi.OnUpdateRiskByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const risk = res?.data?.onUpdateRiskByEntityId;
        if (!risk?.assessmentId || !risk?.id) {
          return;
        }
        this.updateRiskInCache(risk);
        this.onRiskUpdated.next({ type: 'updated', risk });
      },
      error: (err: any) => console.error('[RootEntityContext] risk update subscription error:', err),
    });

    // 8. Risk DELETEs — drop the risk from the cached list.
    this._riskDeleteSub = this.customApi.OnDeleteRiskByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const risk = res?.data?.onDeleteRiskByEntityId;
        if (!risk?.assessmentId || !risk?.id) {
          return;
        }
        this.removeRiskFromCache(risk);
        this.onRiskUpdated.next({ type: 'deleted', risk });
      },
      error: (err: any) => console.error('[RootEntityContext] risk delete subscription error:', err),
    });

    // 9. RiskGroup CREATEs — cache the new raw group under this root (guarded to RiskGroup __typename).
    this._riskGroupCreateSub = this.customApi.OnCreateRiskGroupByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const group = res?.data?.onCreateRiskGroupByEntityId;
        if (!group?.id || !group?.entityId) {
          return;
        }
        this.updateRiskGroupInCache(group);
        this.onRiskGroupUpdated.next({ type: 'created', group });
      },
      error: (err: any) => console.error('[RootEntityContext] risk group create subscription error:', err),
    });

    // 10. RiskGroup UPDATEs — patch the cached raw group in place.
    this._riskGroupUpdateSub = this.customApi.OnUpdateRiskGroupByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const group = res?.data?.onUpdateRiskGroupByEntityId;
        if (!group?.id || !group?.entityId) {
          return;
        }
        this.updateRiskGroupInCache(group);
        this.onRiskGroupUpdated.next({ type: 'updated', group });
      },
      error: (err: any) => console.error('[RootEntityContext] risk group update subscription error:', err),
    });

    // 11. RiskGroup DELETEs — drop the group from the cache.
    this._riskGroupDeleteSub = this.customApi.OnDeleteRiskGroupByEntityIdListener(token, rootEntityId).subscribe({
      next: (res: any) => {
        const group = res?.data?.onDeleteRiskGroupByEntityId;
        if (!group?.id || !group?.entityId) {
          return;
        }
        this.removeRiskGroupFromCache(group.id, group.entityId);
        this.onRiskGroupUpdated.next({ type: 'deleted', group });
      },
      error: (err: any) => console.error('[RootEntityContext] risk group delete subscription error:', err),
    });

    // 12. Group UPDATEs (pillars/reporting groups) — the calcGroupScores lambda recalculates and
    //    UPDATEs Group rows after answers/tags change; without this the `_groups` cache (loaded once,
    //    then latched via _groupsLoaded) served stale scores until a full page reload.
    this._groupUpdateSub = this.customApi.OnUpdateGroupListener(token, { rootId: { eq: rootEntityId } }).subscribe({
      next: (res: any) => {
        const group = res?.data?.onUpdateGroup;
        if (!group?.id) {
          return;
        }
        if (this._groupsLoaded) {
          const idx = this._groups.findIndex(g => g.id === group.id);
          if (idx > -1) {
            const prev = this._groups[idx];
            const merged = { ...prev, ...group };
            // Never let a sparse payload wipe hydrated values.
            if (group.score == null && prev?.score != null) {
              merged.score = prev.score;
            }
            if (group.tags == null && prev?.tags != null) {
              merged.tags = prev.tags;
            }
            this._groups[idx] = merged;
          } else {
            // Update event for a group we haven't cached (e.g. created elsewhere) — register it.
            this._groups.push(group);
          }
        }
        this.onGroupUpdated.next(group);
      },
      error: (err: any) => console.error('[RootEntityContext] group update subscription error:', err),
    });

    // 13. VendorsDetail UPDATEs — a vendor's status / scan / scores / probability / impact /
    //     standardsQualify / timeline live on the VendorsDetail row (1:1 with the vendor Entity via
    //     @hasOne(fields:["id"]) so its id === vendor id), NOT on the Entity row, so OnUpdateEntity never
    //     carries them. Patch the cached vendor's vendorDetails in place so the third-party
    //     list / overview / heatmap stay consistent after tab edits (forth-party, intelligence, artifacts)
    //     without a reload. No server-side rootId filter exists; vendors outside this root simply aren't in
    //     _assessmentMap, so the lookup no-ops.
    this._vendorsDetailSub = this.customApi.OnUpdateVendorsDetailListener(token).subscribe({
      next: (res: any) => {
        const detail = res?.data?.onUpdateVendorsDetail;
        if (!detail?.id) {
          return;
        }
        const entry = Object.values(this._assessmentMap).find(
          e => e.subEntity?.id === detail.id && e.subEntity?.entityType === EntityTypeEnum.VENDOR
        );
        if (entry?.subEntity) {
          const merged: any = { ...(entry.subEntity.vendorDetails ?? {}) };
          Object.keys(detail).forEach(key => {
            if (detail[key] !== null && detail[key] !== undefined) {
              merged[key] = detail[key];
            }
          });
          entry.subEntity.vendorDetails = merged;
        }
      },
      error: (err: any) => console.error('[RootEntityContext] vendors detail subscription error:', err),
    });
  }

  /**
   * Cancel all active AppSync subscriptions.
   *
   * Called automatically by `initSubscriptions` on re-init. Should also be
   * called from the `ngOnDestroy` of whichever component owns the root-entity
   * lifecycle (e.g. the top-level entity host or auth guard).
   */
  teardownSubscriptions(): void {
    this._entitySub?.unsubscribe();
    this._assessmentSub?.unsubscribe();
    this._frameworkScoreSub?.unsubscribe();
    this._frameworkScoreCreateSub?.unsubscribe();
    this._asfSub?.unsubscribe();
    this._asfCreateSub?.unsubscribe();
    this._riskCreateSub?.unsubscribe();
    this._riskUpdateSub?.unsubscribe();
    this._riskDeleteSub?.unsubscribe();
    this._riskGroupCreateSub?.unsubscribe();
    this._riskGroupUpdateSub?.unsubscribe();
    this._riskGroupDeleteSub?.unsubscribe();
    this._groupUpdateSub?.unsubscribe();
    this._vendorsDetailSub?.unsubscribe();
    this._rootEntitySub?.unsubscribe();

    this._entitySub = null;
    this._assessmentSub = null;
    this._frameworkScoreSub = null;
    this._frameworkScoreCreateSub = null;
    this._asfSub = null;
    this._asfCreateSub = null;
    this._riskCreateSub = null;
    this._riskUpdateSub = null;
    this._riskDeleteSub = null;
    this._riskGroupCreateSub = null;
    this._riskGroupUpdateSub = null;
    this._riskGroupDeleteSub = null;
    this._groupUpdateSub = null;
    this._vendorsDetailSub = null;
    this._rootEntitySub = null;
  }
}
